import type { TranslationKey } from "../index";

export const fr: Record<TranslationKey, string> = {
    // --- common — short words/phrases reused verbatim across unrelated components ------------------
    "common.brandHomeAriaLabel": "SoundCheckBox — accueil",
    "common.close": "Fermer",
    "catalogCard.open": "Fiche du catalogue",
    "catalogCard.openAlternatives": "Fiches des alternatives",
    "catalogCard.alternatives": "Alternatives",
    "catalogCard.type": "Type",
    "catalogCard.category": "Catégorie",
    "catalogCard.weight": "Poids",
    "catalogCard.power": "Puissance",
    "about.heading": "À propos de cette version",
    "about.web": "Web",
    "about.api": "API",
    "about.schema": "Contrat du schéma",
    "about.unknown": "inconnu",
    "about.unstamped": "version sans empreinte",
    "about.envProduction": "Production",
    "about.envNotProduction": "{env} — pas la production",
    "about.mismatch": "Le bundle web et l'API proviennent de commits différents. Le déploiement n'est arrivé qu'à moitié : ce que vous voyez et ce qui répond ne sont pas le même code.",
    "common.cancel": "Annuler",
    "common.delete": "Supprimer",
    "ridersList.deletePublished.heading": "Ce rider est publié",
    "ridersList.deletePublished.body": "Le supprimer désactive aussi son lien public. Toute personne qui le détient — une salle, un loueur, celui qui a scanné le QR imprimé — ne pourra plus l'ouvrir, et cela ne peut pas être rétabli.",
    "ridersList.deletePublished.dontAskAgain": "Ne plus demander pendant cette session",
    "common.deletePermanentlyPrompt": "Supprimer définitivement ?",
    "common.yesDelete": "Oui, supprimer",
    "common.loading": "Chargement…",
    "common.saving": "Enregistrement…",
    "common.sending": "Envoi…",

    // --- side-nav (components/side-nav.ts) ----------------------------------------------------------
    "sideNav.home": "Accueil",
    "sideNav.soundman": "Ingénieur du son",
    "sideNav.soundmanComingSoon": "Ingénieur du son — bientôt disponible",
    "sideNav.myRiders": "Mes Fiches Techniques",
    "sideNav.offers": "Offres",
    "myEquipment.warehouse.heading": "Mon entrepôt",
    "myEquipment.warehouse.search": "Rechercher dans mon entrepôt",
    "myEquipment.warehouse.add": "Ajouter à mon entrepôt",
    "myEquipment.warehouse.formed": "Mon entrepôt est constitué — les offres proposent d’abord depuis lui",
    "myEquipment.backToOffer": "← Retour à l’offre",
    "myEquipment.fromOffer.line": "Depuis {offer} pour le rider {rider}, ajouté à votre entrepôt : {items}.",
    "myEquipment.fromOffer.grow": "Agrandissez votre entrepôt :",
    "myEquipment.fromOffer.pick": "Choisir dans notre catalogue",
    "myEquipment.fromOffer.import": "Importer votre liste",
    "myEquipment.fromOffer.dismiss": "Masquer",
    "myEquipment.weight.notSpecified": "Non précisé",
    "myEquipment.col.manufacturer": "Fabricant",
    "myEquipment.sort.by": "Trier par",
    "myEquipment.sort.category": "Catégorie",
    "myEquipment.more": "Plus",
    "myEquipment.searchClose": "Fermer la recherche",
    "sideNav.inbox": "Boîte de réception",
    "sideNav.inboxComingSoon": "Boîte de réception — bientôt disponible",
    "sideNav.outbox": "Boîte d'envoi",
    "sideNav.outboxComingSoon": "Boîte d'envoi — bientôt disponible",
    "sideNav.eventManager": "Organisateur d'événements",
    "sideNav.eventManagerComingSoon": "Organisateur d'événements — bientôt disponible",
    "sideNav.warehouse": "Matériel",
    "sideNav.warehouseComingSoon": "Matériel — bientôt disponible",
    "sideNav.myProfile": "Mon profil",
    "sideNav.admin": "Administration",
    "sideNav.designThemeLabel": "Thème visuel",
    "sideNav.languageLabel": "Langue",
    "sideNav.mainAriaLabel": "Menu principal",

    // --- app-header (components/app-header.ts) ------------------------------------------------------
    "appHeader.adminTitle": "Administrateur",
    "appHeader.adminBadge": "admin",
    "appHeader.logoutTitle": "Se déconnecter",
    "appHeader.logoutLabel": "Déconnexion",

    // --- verify-email banner (components/verify-email-banner.ts) -------------------------------------
    "verifyBanner.message": "Votre e-mail n'est pas encore confirmé — l'enregistrement, la publication et les autres modifications sont désactivés jusqu'à ce que vous le vérifiiez.",
    "verifyBanner.profileLink": "Aller au profil",
    "verifyBanner.resend": "Renvoyer l'e-mail de confirmation",
    "verifyBanner.resendSent": "Envoyé ✓",
    "verifyBanner.resendFailed": "Impossible de renvoyer l'e-mail de confirmation — réessayez depuis votre profil.",
    "verifyBanner.dismissAriaLabel": "Ignorer",

    // --- write errors shown in place of a raw API error (api/http.ts#isEmailNotVerifiedError) --------
    "errors.emailNotVerified": "Vérifiez votre e-mail avant de faire des modifications — allez sur votre profil pour renvoyer le lien.",

    // --- login (pages/login-page.ts) -----------------------------------------------------------------
    "login.hint": "Connectez-vous ou créez un compte pour créer et enregistrer vos fiches techniques.",
    "login.hintRegister": "Créez un compte — un e-mail et un mot de passe suffisent, et votre travail vous suit.",
    "login.emailLabel": "E-mail ou identifiant",
    "login.passwordLabel": "Mot de passe",
    "login.submit": "Se connecter",
    "login.register": "S'inscrire",
    "login.consentNotice": "En créant un compte, vous acceptez nos {termsLink} et notre {privacyLink}.",
    "login.termsLink": "conditions d'utilisation",
    "login.privacyLink": "politique de confidentialité",
    "legal.privacyTitle": "Politique de confidentialité",
    "legal.termsTitle": "Conditions d'utilisation",
    "legal.unavailable": "Ce document n’a pas pu être chargé. Veuillez réessayer plus tard.",
    "consent.title": "Veuillez consulter nos conditions",
    "consent.body": "Pour continuer à utiliser SoundCheckBox, veuillez accepter nos {termsLink} et notre {privacyLink}.",
    "consent.changeNote": "Nous pouvons mettre à jour ces documents. Si un changement ne vous convient pas, vous pouvez cesser d’utiliser le service.",
    "consent.agree": "J’accepte",
    "consent.decline": "Se déconnecter",
    "consent.saveFailed": "Nous n’avons pas pu enregistrer votre accord. Veuillez réessayer.",
    "editor.publishConfirm": "Ce rider sera publié via un lien public que n’importe qui peut ouvrir, sans compte, et il restera accessible jusqu’à ce que vous le dépubliiez. Tout ce qu’il contient, y compris les noms, téléphones et adresses e-mail, deviendra public. Publier ?",
    "editor.publishImpact.offer": "Ce rider figure dans une offre pas encore conclue ({shows}). Publier une nouvelle version modifiera cette offre — le prestataire répondra de nouveau aux lignes changées. Publier ?",
    "editor.publishImpact.contract": "Ce rider relève d'une offre conclue ({shows}). Il ne peut y être changé sans modifier le contrat du spectacle : le prestataire devra confirmer la différence et l'équipe verra la nouvelle version. Publier ?",
    "editor.publishImpact.unnamedShow": "un spectacle sans nom",
    "editor.unpublishConfirm": "Dépublier supprime TOUS les liens publics de ce rider — cette version et toutes les précédentes — et quiconque a déjà reçu un lien ne pourra plus l’ouvrir. C’est irréversible : republier crée un NOUVEAU lien. Dépublier ?",
    "editor.publishDirty.heading": "Publier des modifications non enregistrées ?",
    "editor.publishDirty.body": "C’est la dernière version enregistrée de ce rider qui est publiée, et vos dernières modifications ne sont pas encore enregistrées. Enregistrez-les d’abord pour les publier.",
    "editor.publishDirty.saveAndPublish": "Enregistrer et publier",
    "login.minAge": "Vous devez avoir au moins 16 ans pour créer un compte.",
    "admin.title": "Console d’administration",
    "admin.notAvailable": "Cette page est réservée aux administrateurs.",
    "admin.users.title": "Utilisateurs",
    "admin.users.searchPlaceholder": "Rechercher par e-mail ou identifiant",
    "admin.users.searchBtn": "Rechercher",
    "admin.users.showAdmins": "Afficher les administrateurs",
    "admin.users.browseAll": "Tout parcourir",
    "admin.users.prevPage": "Précédent",
    "admin.users.nextPage": "Suivant",
    "admin.users.pageOf": "Page {page} sur {pages} — {total} comptes",
    "admin.users.searchTooShort": "Saisissez au moins 2 caractères.",
    "admin.users.noMatch": "Aucun compte correspondant.",
    "admin.users.colEmail": "E-mail",
    "admin.users.colLogin": "Identifiant",
    "admin.users.colJoined": "Inscription",
    "admin.users.colVerified": "Vérifié",
    "admin.users.colAdmin": "Admin",
    "admin.users.colActions": "Actions",
    "admin.users.verify": "Marquer vérifié",
    "admin.users.grantAdmin": "Nommer admin",
    "admin.users.revokeAdmin": "Retirer admin",
    "admin.users.verified": "E-mail marqué comme vérifié.",
    "admin.users.granted": "Accès administrateur accordé.",
    "admin.users.revoked": "Accès administrateur retiré.",
    "admin.users.confirmGrant": "Accorder l’accès administrateur à {user} ?",
    "admin.users.confirmRevoke": "Retirer l’accès administrateur à {user} ?",
    "admin.users.deletionPending": "suppression demandée",
    "admin.users.tempPassword": "Mot de passe temporaire",
    "admin.users.tempPasswordPending": "mot de passe temporaire",
    "admin.users.confirmTempPassword": "Émettre un mot de passe temporaire pour {user} ? Son mot de passe actuel cessera de fonctionner immédiatement.",
    "admin.users.tempPasswordIssued": "Mot de passe temporaire de {user}. Copiez-le maintenant, il ne pourra pas être réaffiché :",
    "admin.users.tempPasswordDone": "Mot de passe temporaire émis.",
    // --- users tab: sort, filters, summary (SCRUM-570) ---
    "admin.users.colLastLogin": "Dernière connexion",
    "admin.users.colRiders": "Riders",
    "admin.users.colLastRiderEdit": "Dernière modification de rider",
    "admin.users.sortBy": "Trier par {column}",
    "admin.users.noLoginRecorded": "Non enregistré. Les connexions ne sont suivies que depuis octobre 2026 ; avant, rien n’était conservé, donc ce tiret ne veut pas dire que le compte n’a jamais servi.",
    "admin.users.noRiderEdit": "Ce compte n’a aucun rider, donc aucune modification à ce jour.",
    "admin.users.filtersTitle": "Filtres",
    "admin.users.filterAll": "Tous",
    "admin.users.filterRole": "Rôle",
    "admin.users.filterRoleUsers": "Utilisateurs",
    "admin.users.filterRoleAdmins": "Administrateurs",
    "admin.users.filterVerified": "E-mail vérifié",
    "admin.users.filterVerifiedYes": "Vérifié",
    "admin.users.filterVerifiedNo": "Non vérifié",
    "admin.users.filterRiders": "Riders",
    "admin.users.filterRidersWith": "Avec riders",
    "admin.users.filterRidersWithout": "Sans rider",
    "admin.users.filterActiveDays": "A modifié un rider dans les … derniers jours",
    "admin.users.filterJoinedDays": "Inscrit dans les … derniers jours",
    "admin.users.filterAnyDays": "indifférent",
    "admin.users.clearFilters": "Effacer les filtres",
    "admin.stats.title": "Synthèse",
    "admin.stats.filteredHint": "Les chiffres portent sur les comptes correspondant aux filtres ci-dessus.",
    "admin.stats.unfilteredHint": "Les chiffres portent sur tous les comptes.",
    "admin.stats.total": "Comptes",
    "admin.stats.admins": "Administrateurs",
    "admin.stats.verified": "Vérifiés",
    "admin.stats.withRiders": "Avec riders",
    "admin.stats.ridersTotal": "Riders au total",
    "admin.stats.windowHeader": "Dans les derniers…",
    "admin.stats.days7": "7 jours",
    "admin.stats.days30": "30 jours",
    "admin.stats.days90": "90 jours",
    "admin.stats.days365": "365 jours",
    "admin.stats.activeRiderAuthors": "Ont modifié un rider",
    "admin.stats.newUsers": "Se sont inscrits",
    "admin.stats.loggedIn": "Se sont connectés",
    "admin.stats.loginNote": "Les connexions ne sont enregistrées que depuis octobre 2026. La ligne « Se sont connectés » compte à partir de ce jour ; les sessions antérieures n’y figurent pas.",
    "admin.erasure.title": "Comptes à effacer",
    "admin.erasure.hint": "Demandes de suppression dont le délai d'attente est écoulé. Rien n'est effacé automatiquement : l'effacement est irréversible et supprime le compte avec tous ses riders.",
    "admin.erasure.none": "Rien en attente.",
    "admin.erasure.colRequested": "Demandé le",
    "admin.erasure.colDue": "Échu depuis",
    "admin.erasure.erase": "Effacer",
    "admin.erasure.confirmPrompt": "Saisissez {user} pour effacer ce compte et tous ses riders. Action irréversible.",
    "admin.erasure.confirmMismatch": "L'adresse ne correspond pas — rien n'a été effacé.",
    "admin.erasure.erased": "{user} a été effacé.",
    "admin.content.legalTitle": "Documents juridiques",
    "admin.content.legalHint": "Remplace le document livré avec le site. Vide = le fichier du dépôt est utilisé, comme avant. Markdown.",
    "admin.content.mailTitle": "Textes des e-mails",
    "admin.content.mailHint": "Remplace des chaînes précises des e-mails, par langue. Le champ contient le texte actuellement en vigueur — modifiez-le directement. Enregistrer sans rien changer conserve la version livrée ; « Rétablir » annule votre modification. Les variables entre accolades doivent être conservées telles quelles : elles sont remplies à l'envoi.",
    "admin.content.localeLabel": "Langue",
    "admin.content.usingFile": "version livrée",
    "admin.content.edited": "modifié ici",
    "admin.content.legalPlaceholder": "Vide — le site affiche le document du dépôt.",
    "admin.content.colKey": "Clé",
    "admin.content.colText": "Texte",
    "admin.content.save": "Enregistrer",
    "admin.content.revert": "Rétablir",
    "admin.content.saved": "Enregistré.",
    "admin.content.reverted": "Version livrée rétablie.",
    "admin.content.confirmRevert": "Abandonner cette modification et revenir au document livré ?",
    "admin.content.uiTitle": "Textes de l'interface",
    "admin.content.uiHint": "Remplace des chaînes précises de l'interface, par langue, sans recompilation. Le champ contient le texte actuellement en vigueur — modifiez-le directement. Enregistrer sans rien changer conserve la version livrée ; « Rétablir » annule votre modification. Les changements apparaissent après rechargement.",
    "admin.content.uiSearchPlaceholder": "Rechercher par clé ou texte",
    "admin.content.uiEditedOnly": "modifiées seulement",
    "admin.content.uiStart": "Tapez quelque chose, ou cochez « modifiées seulement ».",
    "admin.content.uiTooMany": "{count} résultats — les 200 premiers sont affichés. Affinez la recherche.",
    "admin.content.badPlaceholders": "Variables inconnues : {list}. Elles s'afficheraient telles quelles.",
    "admin.content.savedReload": "Enregistré. Rechargez la page pour le voir.",
    "admin.tabs.users": "Comptes",
    "admin.tabs.content": "Textes",
    "admin.tabs.settings": "Réglages",
    "admin.content.history": "Historique",
    "admin.content.noHistory": "Aucune version antérieure pour l'instant.",
    "admin.content.restore": "Charger cette version",
    "admin.content.restoredIntoEditor": "Chargée dans l'éditeur — vérifiez puis enregistrez.",
    "admin.settings.title": "Paramètres du site",
    "admin.settings.hint": "Les changements prennent effet immédiatement. Un paramètre sans surcharge utilise la valeur fournie par le code.",
    "admin.settings.save": "Enregistrer",
    "admin.settings.reset": "Réinitialiser",
    "admin.settings.saved": "{key} enregistré.",
    "admin.settings.wasReset": "{key} est revenu à sa valeur par défaut.",
    "admin.settings.overridden": "modifié (défaut : {default})",
    "admin.settings.isDefault": "par défaut",
  "admin.models.title": "Modèles 3D inutilisés",
  "admin.models.hint": "Fichiers .glb téléversés qu’aucun préréglage ni aucun rider enregistré ne référence. La suppression est définitive : il n’existe pas d’historique de téléversement pour restaurer.",
  "admin.models.scan": "Analyser",
  "admin.models.scanning": "Analyse…",
  "admin.models.none": "Rien à nettoyer : tous les modèles téléversés sont utilisés.",
  "admin.models.summary": "{count} inutilisés, {size} au total. {inUse} encore utilisés. {presets} préréglages et {riders} riders analysés.",
  "admin.models.colFile": "Fichier",
  "admin.models.colSize": "Taille",
  "admin.models.colModified": "Téléversé",
  "admin.models.deleteSelected": "Supprimer la sélection",
  "admin.models.deleteSelectedCount": "Supprimer la sélection ({count})",
  "admin.models.confirmDelete": "Supprimer définitivement {count} fichier(s) de modèle ? Action irréversible.",
  "admin.models.deleted": "{count} fichier(s) supprimé(s), {size} libéré(s).",
  "admin.models.skipped": "{count} ignoré(s) — ils sont désormais référencés.",
    "login.forgotPassword": "Mot de passe oublié ?",
    "login.sessionExpired": "Votre session a expiré — veuillez vous reconnecter.",
    "login.invalidCredentials": "E-mail/identifiant ou mot de passe incorrect.",

    // --- recovery (pages/recovery-pages.ts) -----------------------------------------------------------
    "recovery.forgotTitle": "Réinitialisez votre mot de passe",
    "recovery.forcedTitle": "Choisissez votre mot de passe",
    "recovery.forcedLead": "Vous vous êtes connecté avec un mot de passe fourni par le support. Définissez le vôtre pour continuer : rien d'autre n'est accessible avant.",
    "recovery.forcedCurrentLabel": "Mot de passe temporaire",
    "recovery.forcedSubmit": "Enregistrer le mot de passe",
    "recovery.forgotLead": "Entrez votre adresse e-mail et nous vous enverrons un lien pour choisir un nouveau mot de passe.",
    "recovery.emailLabel": "E-mail",
    "recovery.forgotSubmit": "Envoyer le lien de réinitialisation",
    "recovery.backToSignIn": "Retour à la connexion",
    "recovery.forgotSuccess": "Si cette adresse est associée à un compte, un lien de réinitialisation est en route. Vérifiez votre boîte de réception et vos spams.",
    "recovery.resetIncompleteTitle": "Le lien de réinitialisation est incomplet",
    "recovery.resetIncompleteLead": "Ce lien ne contient pas de jeton de validation. Demandez-en un nouveau.",
    "recovery.resetAgainLink": "Demander un nouveau lien de réinitialisation",
    "recovery.resetTitle": "Choisissez un nouveau mot de passe",
    "recovery.newPasswordLabel": "Nouveau mot de passe",
    "recovery.repeatPasswordLabel": "Répétez le nouveau mot de passe",
    "recovery.resetSubmit": "Définir le nouveau mot de passe",
    "recovery.passwordMismatch": "Les deux mots de passe ne correspondent pas.",
    "recovery.resetSuccess": "Mot de passe mis à jour. Redirection vers la page de connexion…",
    "recovery.verifyTitle": "Confirmation de votre adresse e-mail",
    "recovery.verifyChecking": "Vérification de votre lien…",
    "recovery.continueToSignIn": "Continuer vers la connexion",
    "recovery.verifyNoToken": "Ce lien ne contient pas de jeton. Demandez-en un nouveau depuis votre profil.",
    "recovery.verifySuccess": "Votre e-mail est confirmé. Vous pouvez maintenant vous connecter.",
    "recovery.verifyFailed": "Ce lien de confirmation est invalide ou a expiré. Connectez-vous et demandez-en un nouveau depuis votre profil.",
    "recovery.verifyChangeTitle": "Confirmation de votre nouvelle adresse e-mail",
    "recovery.verifyChangeNoToken": "Ce lien ne contient pas de jeton. Demandez-en un nouveau depuis votre profil.",
    "recovery.verifyChangeSuccess": "Votre nouvelle adresse e-mail est confirmée — connectez-vous désormais avec elle.",
    "recovery.verifyChangeFailed": "Ce lien de confirmation est invalide ou a expiré. Connectez-vous et demandez un nouveau changement d'e-mail depuis votre profil.",

    // --- landing (pages/landing-page.ts) --------------------------------------------------------------
    "landing.nav.try": "Essayer",
    "landing.nav.eventManager": "Pour Organisateurs",
    "landing.nav.rentCompany": "Pour Prestataires",
    "landing.nav.soundman": "Pour Sonorisateurs",
    "landing.nav.howItWorks": "Comment ça marche",
    "landing.nav.contacts": "Contacts",
    "landing.nav.myRiders": "Mes fiches techniques",
    "landing.nav.login": "Se connecter",
    "landing.nav.signup": "Inscription gratuite",
    "landing.hero.title": "Concevez votre spectacle",
    "landing.hero.sub": "Service en ligne pour louer tout le matériel scénique nécessaire. Créez une fiche technique avec tous les détails et trouvez des prestataires en un clic.",
    "landing.hero.cta": "Ouvrir l'éditeur",
    "landing.hero.mediaAlt": "L'éditeur de fiches techniques 3D SoundCheckBox",
    "landing.try.title": "Créez une fiche technique maintenant",
    "landing.try.lead": "Aucun compte nécessaire. L'éditeur s'ouvre immédiatement et votre travail reste dans ce navigateur — un compte ne sert qu'à le conserver, le partager ou l'envoyer.",
    "landing.try.cta": "Ouvrir l'éditeur",
    "landing.eventManager.title": "Pour les organisateurs d'événements",
    "landing.eventManager.body": "Vous gérez un festival, une tournée ou un événement privé ? SoundCheckBox regroupe tous les accords entre artistes et prestataires au même endroit.",
    "landing.eventManager.feature1Title": "Attribution du matériel",
    "landing.eventManager.feature1Desc": "Chaque demande de fiche technique est comparée aux stocks réels des fournisseurs.",
    "landing.eventManager.feature2Title": "Comparaison d'offres",
    "landing.eventManager.feature2Desc": "Comparez les options côte à côte plutôt que de multiplier les e-mails.",
    "landing.eventManager.feature3Title": "Suivi des engagements",
    "landing.eventManager.feature3Desc": "Consultez la fiabilité et les avis sur les prestataires avant de vous engager.",
    "landing.eventManager.feature4Title": "Paiement en ligne",
    "landing.eventManager.feature4Desc": "Réglez la réservation directement depuis la plateforme.",
    "landing.eventManager.mediaAlt": "Flux de travail de l'organisateur",
    "landing.rentCompany.title": "Pour les sociétés de location",
    "landing.rentCompany.body": "Un assistant automatisé qui lit la fiche technique reçue, la vérifie par rapport à votre inventaire et chiffrez le projet automatiquement.",
    "landing.rentCompany.feature1Title": "Analyse d'inventaire",
    "landing.rentCompany.feature1Desc": "Les demandes sont comparées à votre stock réel en entrepôt.",
    "landing.rentCompany.feature2Title": "Tarification automatique",
    "landing.rentCompany.feature2Desc": "Devis calculés sur la base de vos tarifs dès la réception de la fiche technique.",
    "landing.rentCompany.feature3Title": "Grandes opérations",
    "landing.rentCompany.feature3Desc": "Conçu pour les volumes importants, compatible avec vos logiciels de gestion.",
    "landing.rentCompany.feature4Title": "Sous-location",
    "landing.rentCompany.feature4Desc": "Un équipement manque ? Trouvez immédiatement un partenaire qui l'a en stock.",
    "landing.rentCompany.mediaAlt": "Flux de travail de la société de location",
    "landing.soundman.title": "Pour les ingénieurs du son",
    "landing.soundman.body": "Moins de paperasse, plus de temps pour la musique. Créez votre fiche technique à partir de configurations éprouvées et réutilisez-les.",
    "landing.soundman.feature1Title": "Configurations validées",
    "landing.soundman.feature1Desc": "Partez d'un plan de scène fonctionnel plutôt que d'une page blanche.",
    "landing.soundman.feature2Title": "Vos propres préréglages",
    "landing.soundman.feature2Desc": "Enregistrez un groupe de matériel personnalisé une fois, réutilisez-le à volonté.",
    "landing.soundman.feature3Title": "Gestion multi-groupes",
    "landing.soundman.feature3Desc": "Gérez tous les artistes dont vous vous occupez sur un seul compte.",
    "landing.soundman.feature4Title": "Propositions de kits",
    "landing.soundman.feature4Desc": "Les prestataires proposent des packs complets adaptés à votre fiche technique.",
    "landing.soundman.mediaAlt": "Flux de travail de l'ingénieur du son",
    "landing.howItWorks.videoTitle": "SoundCheckBox — comment ça marche",
    "landing.howItWorks.lead": "Placez le matériel sur une scène 3D : le patch, la liste de sorties et la liste de matériel se génèrent automatiquement. Imprimez ou envoyez directement aux prestataires.",
    "landing.howItWorks.feature1Title": "1. Montez la scène",
    "landing.howItWorks.feature1Desc": "Faites glisser les éléments (batterie, backline, micros, retours) sur une scène à l'échelle.",
    "landing.howItWorks.feature2Title": "2. Les listes se génèrent seules",
    "landing.howItWorks.feature2Desc": "Canaux, sources et équipements découle de la disposition sur scène.",
    "landing.howItWorks.feature3Title": "3. Partagez-la",
    "landing.howItWorks.feature3Desc": "Imprimez un document A4 clair avec le plan de scène ou envoyez un lien au prestataire.",
    "landing.contact.title": "Contactez-nous",
    "landing.contact.emailLabel": "E-mail",
    "landing.contact.addressLabel": "Adresse",
    "landing.contact.followUs": "Suivez-nous",
    "landing.contact.nameFieldLabel": "Votre nom",
    "landing.contact.emailFieldLabel": "Votre e-mail",
    "landing.contact.messageLabel": "Message",
    "landing.contact.submit": "Envoyer le message",
    "landing.contact.captchaNote": "Protégé par Cloudflare Turnstile — la {privacyLink} et les {termsLink} de Cloudflare s'appliquent.",
    "landing.contact.privacyPolicyLinkText": "Politique de confidentialité",
    "landing.contact.termsLinkText": "Conditions d'utilisation",
    "landing.contact.validationError": "Veuillez indiquer votre nom, votre e-mail et votre message.",
    "landing.contact.success": "Merci — votre message est en route. Nous vous répondrons à l'adresse indiquée.",
    "landing.footer.tagline": "SoundCheckBox — éditeur de fiches techniques scéniques",

    // --- riders list (pages/riders-list-page.ts) ------------------------------------------------------
    "riders.pageTitle": "Mes fiches techniques",
    "riders.newRiderButton": "Nouvelle Fiche",
    "riders.filter.groupLabel": "Filtrer les fiches techniques par statut",
    "riders.filter.all": "Toutes",
    "riders.filter.empty": "Aucune fiche technique ne correspond à ce filtre.",
    "riders.table.title": "Titre",
    "riders.table.band": "Groupe / Artiste",
    "riders.table.created": "Créée le",
    "riders.table.changed": "Modifiée le",
    "riders.table.status": "Statut",
    "riders.table.action": "Action",
    "riders.table.empty": "Aucune fiche technique pour l'instant — cliquez sur « + Nouvelle Fiche » pour créer la première.",
    "riders.status.published": "Publiée",
    "riders.status.draft": "Brouillon",
    "riders.status.publicLinkTitle": "Lien public : {url}",
    "riders.actions.edit": "Éditer",
    "riders.actions.moreActionsAriaLabel": "Plus d'actions pour {title}",
    "riders.actions.publish": "Publier",
    "riders.actions.unpublish": "Dépublier",
    "riders.actions.print": "Imprimer",
    "riders.actions.copyPublicLink": "Copier le lien public",
    "riders.actions.rename": "Renommer",
    "riders.actions.duplicate": "Dupliquer",
    "riders.copySuffix": "(copie)",
    "riders.copySuffixN": "(copie {n})",
    "riders.rename.heading": "Renommer le rider",
    "riders.rename.nameLabel": "Nouveau nom",
    "riders.rename.confirm": "Renommer",
    "riders.status.renaming": 'Renommage de « {title} »…',
    "riders.status.renamed": 'Renommé en « {title} ».',
    "riders.status.renameFailed": 'Impossible de renommer « {title} » : {message}',
    "riders.status.duplicating": 'Duplication de « {title} »…',
    "riders.status.duplicated": '« {title} » a été créé.',
    "riders.status.duplicateFailed": 'Impossible de dupliquer « {title} » : {message}',
    "riders.status.deleting": 'Suppression de "{title}"…',
    "riders.status.deleted": '"{title}" a été supprimée.',
    "riders.status.deleteFailed": 'Échec de la suppression de "{title}" : {message}',
    "riders.status.linkCopied": "Lien public copié : {url}",
    "riders.status.unpublished": '"{title}" a été dépubliée.',
    "riders.status.publishSuccess": '"{title}" publiée — lien public : {url}',
    "riders.status.unpublishFailed": 'Échec de la dépublication de "{title}" : {message}',
    "riders.status.publishFailed": 'Échec de la publication de "{title}" : {message}',
    "riders.status.publishedNoPicture": "La page publique n’a pas d’image du plan de scène : cette liste ne peut pas en dessiner. Publiez depuis l’éditeur pour l’ajouter.",
    "riders.status.loading": "Chargement de vos fiches techniques…",
    "riders.status.loadFailed": "Impossible de charger les fiches techniques : {message}",

    // --- profile (pages/profile-page.ts) ---------------------------------------------------------------
    "profile.pageTitle": "Mon profil",
    "profile.personal.title": "Informations personnelles",
    "profile.personal.nameLabel": "Nom",
    "profile.personal.phoneLabel": "Téléphone",
    "profile.personal.countryLabel": "Pays",
    "profile.personal.cityLabel": "Ville",
    "profile.personal.languageLabel": "Langue",
    "profile.personal.save": "Enregistrer les infos personnelles",
    "profile.work.title": "Informations professionnelles",
    "profile.work.companyLabel": "Société / Groupe",
    "profile.work.roleLabel": "Je suis",
    "roleChoice.hint": "Détermine la page qui s'ouvre après la connexion.",
    "roleChoice.hintOptional": "Facultatif. Détermine la page qui s'ouvre après la connexion.",
    "profile.work.role.soundman": "Ingénieur du son",
    "profile.work.role.manager": "Organisateur d'événements",
    "profile.work.role.rental": "Société de location",
    "profile.work.role.other": "Autre",
    "profile.work.save": "Enregistrer les infos pro",
    "profile.defaults.title": "Coordonnées par défaut",
    "profile.defaults.hint": "Ces informations pré-remplissent le bloc de contact de chaque NOUVELLE fiche technique.",
    "profile.defaults.bandNameLabel": "Nom du groupe / artiste",
    "profile.defaults.contactNameLabel": "Nom du contact",
    "profile.defaults.contactPhoneLabel": "Téléphone du contact",
    "profile.defaults.contactEmailLabel": "E-mail du contact",
    "profile.defaults.save": "Enregistrer les valeurs par défaut",
    "profile.security.title": "Sécurité",
    "profile.security.emailLabel": "E-mail",
    "profile.security.emailConfirmedLabel": "E-mail confirmé",
    "profile.security.passwordChangedLabel": "Dernier changement de mot de passe",
    "profile.security.memberSinceLabel": "Membre depuis",
    "profile.security.emailVerifiedYes": "Oui",
    "profile.security.emailVerifiedNot": "Pas encore",
    "profile.security.resendVerification": "Renvoyer l'e-mail de confirmation",
    "profile.security.changePasswordTitle": "Changer le mot de passe",
    "profile.security.changePasswordSubmit": "Valider le changement de mot de passe",
    "profile.security.currentPasswordLabel": "Mot de passe actuel",
    "profile.security.newPasswordLabel": "Nouveau mot de passe",
    "profile.security.repeatPasswordLabel": "Répéter le nouveau mot de passe",
    "profile.security.resendSent": "Envoyé ✓",
    "profile.security.resendSuccess": "E-mail de confirmation envoyé. Vérifiez votre boîte de réception et vos spams.",
    "profile.security.passwordMismatch": "Les deux nouveaux mots de passe ne correspondent pas.",
    "profile.security.passwordChanged": "Mot de passe modifié. Un e-mail de confirmation vous a été envoyé.",
    "profile.security.changeEmailTitle": "Changer d'e-mail",
    "profile.security.changeEmailHint": "Nous enverrons un lien de confirmation à la nouvelle adresse — rien ne change tant que vous n'avez pas cliqué dessus.",
    "profile.security.newEmailLabel": "Nouvel e-mail",
    "profile.security.currentPasswordForEmailLabel": "Mot de passe actuel",
    "profile.security.changeEmailSubmit": "Envoyer le lien de confirmation",
    "profile.security.changeEmailMissingFields": "Saisissez la nouvelle adresse e-mail et votre mot de passe actuel.",
    "profile.security.changePasswordMissingFields": "Saisissez votre mot de passe actuel et un nouveau mot de passe d'au moins 8 caractères.",
    "profile.security.changeEmailSuccess": "Si {newEmail} est disponible, un lien de confirmation y est en route. Vérifiez la boîte de réception et les spams.",
    "profile.security.changeEmailPending": "Nous avons envoyé un lien de confirmation à {newEmail}. Vous êtes toujours connecté en tant que {currentEmail} jusqu'à sa confirmation.",
    "profile.ai.title": "Assistant IA",
    "profile.ai.hint": "L'assistant dispose d'un petit quota gratuit. Ensuite, ajoutez votre propre clé Google Gemini pour continuer — vous payez Google directement, sans limite ici.",
    "profile.ai.usageFree": "Quota gratuit utilisé : {used} sur {cap}.",
    "profile.ai.usageByok": "Utilisation de votre clé ({hint}) — sans limite.",
    "profile.ai.byokHint": "Collez votre propre clé Gemini API pour continuer à utiliser l'assistant après le quota gratuit.",
    "profile.ai.getKeyLink": "Obtenir une clé gratuite",
    "profile.ai.keyLabel": "Votre clé Gemini API",
    "profile.ai.keyReplace": "saisissez une nouvelle clé pour la remplacer",
    "profile.ai.saveKey": "Enregistrer la clé",
    "profile.ai.clearKey": "Retirer la clé",
    "profile.ai.byokUnavailable": "La clé personnelle n'est pas encore disponible sur ce serveur.",
    "profile.ai.keyRequired": "Saisissez d'abord votre clé d'API.",
    "profile.ai.keyValidating": "Vérification de la clé…",
    "profile.ai.keySaved": "Clé enregistrée — l'assistant l'utilise désormais.",
    "profile.ai.keyCleared": "Clé retirée.",
    "profile.ai.keySaveFailed": "Impossible d'enregistrer la clé : {message}",
    "profile.ai.loadFailed": "Impossible de charger votre utilisation de l'IA.",
    "profile.privacy.title": "Données et confidentialité",
    "profile.privacy.hint": "Vos droits au titre du RGPD et des lois applicables.",
    "profile.privacy.downloadTitle": "Télécharger mes données",
    "profile.privacy.downloadHint": "L'ensemble de vos données enregistrées (y compris toutes vos fiches) dans un fichier JSON.",
    "profile.privacy.downloadButton": "Télécharger",
    "profile.privacy.marketingTitle": "Nouveautés produit",
    "profile.privacy.marketingHint": "Nouvelles occasionnelles sur SoundCheckBox. Les e-mails de service (réinitialisation, confirmation) sont toujours envoyés.",
    "profile.privacy.marketingCheckbox": "Recevoir les e-mails d'information",
    "profile.privacy.deleteTitle": "Supprimer mon compte",
    "profile.privacy.deleteHint": "Supprime définitivement votre compte et l'ensemble de vos fiches techniques.",
    "profile.privacy.deleteButton": "Supprimer le compte",
    "profile.privacy.cancelDeleteButton": "Annuler la suppression",
    "profile.status.loadFailed": "Impossible de charger votre profil : {message}",
    "profile.status.personalSaved": "Informations personnelles enregistrées.",
    "profile.status.workSaved": "Informations professionnelles enregistrées.",
    "profile.status.defaultsSaved": "Valeurs par défaut enregistrées.",
    "profile.privacy.preparingExport": "Préparation de vos données…",
    "profile.privacy.exportDownloaded": "Votre fichier de données a été téléchargé.",
    "profile.privacy.marketingOn": "Vous recevrez les e-mails sur les nouveautés.",
    "profile.privacy.marketingOff": "Vous êtes désabonné des e-mails d'information.",
    "profile.privacy.deleteConfirm": "Supprimer votre compte SoundCheckBox ?\n\nVotre compte et toutes vos fiches seront définitivement effacés après le délai de grâce. Vous pouvez annuler d'ici là.",
    "profile.privacy.deletionScheduled": "Suppression programmée. Les détails vous ont été envoyés par e-mail — vous pouvez toujours annuler.",
    "profile.privacy.deletionCancelled": "Suppression annulée. Votre compte reste actif.",
    "profile.privacy.deletionPendingHint": "Suppression définitive prévue le {date}. Annulable à tout moment avant cette date.",

    // --- public rider viewer (pages/public-viewer-page.ts) ---------------------------------------------
    "publicViewer.headerSub": "fiche technique publique",
    "publicViewer.riderListsAriaLabel": "Listes de la fiche technique",
    "publicViewer.controls.grid": "Grille",
    "publicViewer.controls.titles": "Noms",
    "publicViewer.controls.inputCh": "Entrées (CH)",
    "publicViewer.controls.outputMix": "Sorties (MIX)",
    "publicViewer.controls.source": "Source",
    "publicViewer.controls.comments": "Remarques",
    "publicViewer.window.close": "Fermer la fenêtre",
    "publicViewer.window.zoomIn": "Agrandir",
    "publicViewer.window.zoomOut": "Réduire",
    "publicViewer.controls.scene": "Scène 3D",
    "publicViewer.controls.sceneLoading": "3D (chargement...)",
    "publicViewer.controls.sceneHint": "Voir la scène en trois dimensions — faites glisser pour tourner autour.",
    "publicViewer.controls.plan": "Vue de dessus",
    "publicViewer.controls.planHint": "Regarder la scène à la verticale, depuis la salle.",
    "publicViewer.controls.schematic": "Schéma N&B",
    "publicViewer.schematicLoading": "Tracé du plan…",
    "publicViewer.schematicFailed": "Impossible de charger la bibliothèque de symboles.",
    "publicViewer.controls.schematicHint": "Schéma de scène noir et blanc à fort contraste pour l'impression et la lecture rapide.",
    "publicViewer.controls.schematicDisabledHint": "Pas encore prêt — le plan en noir et blanc est toujours en cours.",
    "publicViewer.loaderAlt": "Chargement des données et des modèles 3D...",
    "publicViewer.controls.hideStage": "Masquer la scène",
    "publicViewer.controls.showStage": "Afficher la scène",
    "publicViewer.controls.hideStageHint": "Masquer la scène pour que les listes et le texte du rider occupent tout l'écran.",
    "publicViewer.controls.viewSwitchAriaLabel": "Vue de la scène",
    "publicViewer.controls.follow": "Zoomer sur la sélection",
    "publicViewer.controls.followHint": "Choisir un canal amène la caméra jusqu'à l'objet source.",
    "publicViewer.controls.labels": "Étiquettes",
    "publicViewer.controls.fit": "Scène entière",
    "publicViewer.controls.fitHint": "Recadrer toute la scène.",
    "publicViewer.controls.print": "Imprimer",
    "publicViewer.stageHint": "Faites glisser pour orbiter, pincez/défilez pour zoomer, appuyez sur un objet pour le sélectionner.",
    "publicViewer.stageHintPlan": "Faites glisser pour vous déplacer, pincez/défilez pour zoomer, appuyez sur un objet pour le sélectionner.",
    "publicViewer.planPosterAlt": "Plan de scène rendu lors de la publication de ce rider",
    "publicViewer.deselectAriaLabel": "Désélectionner",
    "publicViewer.tabs.rider": "Fiche",
    "publicViewer.tabs.input": "Entrées",
    "publicViewer.tabs.output": "Sorties",
    "publicViewer.tabs.gear": "Matériel",
    "publicViewer.bandLogoAlt": "Logo du groupe",
    "publicViewer.table.ch": "Ch",
    "publicViewer.table.source": "Source",
    "publicViewer.table.type": "Type",
    "publicViewer.table.name": "Nom",
    "publicViewer.table.comment": "Remarque",
    "publicViewer.table.category": "Catégorie",
    "publicViewer.table.owner": "Propriétaire",
    "publicViewer.table.count": "Qté",
    "publicViewer.footer": "Réalisé avec",
    "publicViewer.meta.inputCh": "Voies d'entrée (CH)",
    "publicViewer.meta.outputMix": "Circuits de sortie (MIX)",
    "publicViewer.meta.gear": "Matériel",
    "publicViewer.meta.gearCount": "{n} élément(s)",
    "publicViewer.noRiderNotes": "Aucune note sur cette fiche.",
    "publicViewer.untitledRiderHeading": "Fiche sans titre",
    "publicViewer.untitledRiderDocTitle": "Fiche technique",
    "publicViewer.documentTitle": "{title} — SoundCheckBox",
    "publicViewer.contactLabel": "Contact : {name}",
    "publicViewer.telLabel": "Tél : {phone}",
    "publicViewer.emailLabel": "E-mail : {email}",
    "publicViewer.status.noRiderId": "Aucune fiche spécifiée — il manque l'identifiant dans le lien.",
    "publicViewer.status.withdrawn": "L'auteur a retiré ce rider ; ce lien ne fonctionne plus.",
    "common.deletePublishedPrompt": "Supprimer définitivement ? Son lien public cessera de fonctionner pour tous ceux qui l'ont.",
    "common.yesDeleteAndUnpublish": "Oui, supprimer et désactiver le lien",
    "publicViewer.status.notPublished": "Cette fiche n'est pas publiée (ou n'existe pas). Demandez à son propriétaire de la publier.",
    "publicViewer.powerUnavailable": "Résumé de puissance électrique indisponible.",
    "publicViewer.controls.makeOffer": "Faire une offre",
    "publicViewer.controls.makeOfferHint": "Répondez à la liste des canaux et du matériel de cette fiche en tant que prestataire.",
    "publicViewer.controls.compact": "Vue compacte",
    "publicViewer.controls.compactHint": "Le plan noir et blanc avec la liste courte des canaux dessous.",
    "publicViewer.controls.io": "I/O",
    "publicViewer.controls.ioHint": "Afficher les numéros de canal et de mix sur le plan.",
    "publicViewer.controls.more": "Plus",
    "publicViewer.controls.loadingWord": "(chargement...)",
    "publicViewer.phantomTitle": "Alimentation fantôme (+48V)",
    "publicViewer.planFull.ariaLabel": "Ouvrir le plan en plein écran",
    "publicViewer.status.offerOwnRider": "Vous ne pouvez pas faire une offre sur votre propre fiche.",
    "publicViewer.status.offerStartFailed": "Impossible de démarrer l'offre : {error}",

    // --- offer mode (pages/offer-overlay.ts, PR2 — docs/V2_OFFER_TECH_APPROVAL.md) -----------------------
    "offer.loading": "Chargement de l'offre…",
    "offer.loadFailed": "Impossible de charger cette offre : {error}",
    "offer.backToRider": "Retour à la fiche",
    "offer.status.draft": "Brouillon",
    "offer.status.submitted": "Envoyée",
    "offer.status.changesRequested": "Modifications demandées",
    "offer.status.awaitingOrganiser": "En attente de l'organisateur",
    "offer.status.approved": "Approuvée",
    "offer.roundLabel": "Réponses v{n}",
    "offer.commentLabel": "Commentaire général",
    "offer.commentPlaceholder": "Tout ce que l'auteur de la fiche devrait savoir sur cette offre dans son ensemble…",
    "offer.authorCommentHeading": "Modifications demandées par l'auteur de la fiche",
    "offer.declinedReasonHeading": "Motif du refus, de l'auteur de la fiche",
    "offer.markRemainingOk": "Marquer le reste comme OK",
    "offer.saveDraft": "Enregistrer le brouillon",
    "offer.submit": "Envoyer l'offre",
    "offer.saving": "Enregistrement…",
    "offer.savedStatus": "Brouillon enregistré.",
    "offer.submitting": "Envoi…",
    "offer.submitFailed": "Impossible d'envoyer : {error}",
    "offer.saveFailed": "Impossible d'enregistrer : {error}",
    "offer.printFailed": "Impossible d'imprimer : {error}",
    "offer.guest.notice": "Vous n'êtes pas connecté. Vos réponses sont conservées dans ce navigateur jusqu'à l'envoi de l'offre.",
    "offer.myList.button": "Vérifier avec mon entrepôt",
    "offer.myList.empty": "Votre stock est vide : ajoutez votre matériel dans Matériel",
    "offer.myList.link": "Matériel",
    "offer.myList.summary": "{ok} lignes peuvent être marquées OK d'après votre liste, pour {short} lignes vous en avez moins que nécessaire, {missing} ne sont pas dans votre liste",
    "offer.myList.apply": "Appliquer",
    "offer.myList.cancel": "Annuler",
    "offer.myList.short": "vous en avez {have} sur {need}",
    "offer.myList.marker": "depuis ma liste",
    "offer.myList.nothing": "Rien à vérifier : chaque ligne de matériel a déjà une réponse.",
    "offer.guest.signInStep": "Pour l'envoyer, connectez-vous ou créez un compte — vos réponses sont conservées.",
    "offer.guest.signInButton": "Se connecter ou créer un compte",
    "offer.guest.keepEditing": "Continuer à modifier",
    "offer.guest.emailLabel": "Votre e-mail",
    "offer.guest.emailSend": "Envoyer l'offre",
    "offer.guest.emailHint": "Nous vous enverrons un lien par e-mail. Ouvrez-le et l'offre part, sans mot de passe.",
    "offer.guest.emailInvalid": "Saisissez une adresse e-mail valide.",
    "offer.guest.emailSent": "Nous avons envoyé un e-mail à {email}. Ouvrez le lien : votre offre partira toute seule. Vous pouvez fermer cet onglet ; vos réponses sont gardées dans ce navigateur pendant 48 heures.",
    "offer.guest.emailSendFailed": "Impossible d'envoyer l'e-mail pour le moment. Réessayez dans une minute.",
    "offer.guest.emailAlreadySent": "L'e-mail de confirmation a déjà été envoyé à {email}. Ouvrez le lien : votre offre partira toute seule. Regardez aussi dans les spams : un e-mail d'un expéditeur inconnu y atterrit souvent. S'il n'y est pas, redemandez dans {minutes} min.",
    "offer.guest.emailSentAgain": "L'e-mail de confirmation a déjà été envoyé à {email}, et nous l'avons renvoyé. Ouvrez le lien de l'un ou l'autre : votre offre partira toute seule. Regardez aussi dans les spams : un e-mail d'un expéditeur inconnu y atterrit souvent.",
    "offer.autoSending": "Envoi de votre offre…",
    "offer.autoSent": "Votre offre a été envoyée.",
    "offer.awaitingEmail": "Nous avons envoyé un e-mail à {email}. Ouvrez le lien : votre offre partira toute seule.",
    "offer.where.title": "Quand et où — pour que l'offre arrive dans le bon show",
    "offer.where.dateLabel": "Date",
    "offer.where.kind.range": "Période",
    "offer.where.kind.choice": "Plusieurs dates",
    "offer.where.kind.day": "Un jour",
    "offer.where.pick.day": "Touchez un jour.",
    "offer.where.pick.range": "Touchez le premier jour, puis le dernier.",
    "offer.where.pick.choice": "Touchez chaque date possible — au moins deux.",
    "offer.where.clear": "Pas encore de date",
    "offer.where.prevMonth": "Mois précédent",
    "offer.where.nextMonth": "Mois suivant",
    "offer.where.chips": "Sélection rapide",
    "offer.where.weekdays": "Jours possibles",
    "offer.where.chip.thisWeek": "Cette semaine",
    "offer.where.chip.nextWeek": "Semaine prochaine",
    "offer.where.chip.lastWeekOf": "Dernière semaine de {month}",
    "offer.where.chip.wholeMonth": "Mois entier",
    "offer.where.possibleDays": "Jours possibles : {n}",
    "offer.where.removeDate": "Retirer la date",
    "offer.where.venue": "Salle",
    "offer.where.city": "Ville (facultatif)",
    "offer.where.hint": "Le groupe voit votre offre dans son show pour cette date.",
    "offer.where.error.dayMissing": "Choisissez la date.",
    "offer.where.error.rangeIncomplete": "Touchez le dernier jour de la période.",
    "offer.where.error.rangeBackwards": "La plage se termine avant de commencer.",
    "offer.where.error.rangeNoDays": "Tous les jours de la plage sont barrés : cochez au moins un jour de la semaine.",
    "offer.where.error.choiceFew": "Choisissez au moins deux dates ou passez à « Un jour ».",
    "offer.placed.whenVenue": "Votre offre pour ce show ({when}, {venue}) a été envoyée. Les organisateurs du show l'examinent — merci !",
    "offer.placed.when": "Votre offre pour ce show ({when}) a été envoyée. Les organisateurs du show l'examinent — merci !",
    "offer.placed.venue": "Votre offre pour ce show ({venue}) a été envoyée. Les organisateurs du show l'examinent — merci !",
    "offer.placed.none": "Votre offre pour ce show a été envoyée. Les organisateurs du show l'examinent — merci !",
    "offer.showLink.for": "Pour : {parts}.",
    "offer.showLink.archived": "Ce show est déjà confirmé : il n'accepte plus d'offres.",
    "offer.showLink.gone": "Ce lien de show ne fonctionne pas : il a été retiré ou n'a pas pu être lu. Demandez-en un nouveau au groupe.",
    "offer.send.title": "Envoyer cette offre ?",
    "offer.send.rider": "Rider : {title}, v{version}",
    "offer.send.lines": "Lignes : {total}",
    "offer.send.comment": "Votre commentaire général",
    "offer.send.ask": "Vérifiez : indiquez la date et la salle, ou dites qu'elles ne sont pas encore définies.",
    "offer.send.notSet": "Date et salle pas encore définies",
    "offer.send.notSetOn": "Part avec : date et salle pas encore définies.",
    "offer.send.or": "ou",
    "offer.send.send": "Envoyer",
    "offer.send.back": "Retour à l'offre",
    "offer.send.close": "Fermer",
    "offer.send.need": "Indiquez une date et une salle, ou touchez « Date et salle pas encore définies ».",
    "offer.send.viaLink": "La date et la salle viennent du lien du show — rien à remplir.",
    "offer.said.notSet": "Date et salle : pas encore définies (réponse du prestataire)",
    "riders.actions.publishToShow": "Publier dans un show",
    "publishShow.title": "Publier dans un show",
    "publishShow.hint": "Un show a une date et un lieu. Un prestataire qui ouvre le lien répond exactement à ce show.",
    "publishShow.whenWhere": "Quand et où",
    "publishShow.rider": "Rider : {title}, v{version}",
    "publishShow.create": "Créer le show et le lien",
    "publishShow.creating": "Création du show…",
    "publishShow.linkLabel": "Lien pour les prestataires",
    "publishShow.copy": "Copier",
    "share.action": "Envoyer",
    "share.hint": "Envoyez cette fiche par e-mail, WhatsApp, Telegram ou n\u2019importe quelle messagerie",
    "share.email": "E-mail",
    "share.whatsapp": "WhatsApp",
    "share.telegram": "Telegram",
    "share.copyLink": "Copier le lien",
    "share.copied": "Lien copié",
    "share.kind": "fiche technique",
    "publishShow.copied": "Copié",
    "publishShow.close": "Fermer",
    "publishShow.notPublished": "Publiez d'abord le rider : un show pointe vers une version publiée.",
    "publishShow.failed": "Impossible de créer le show : {error}",
    "showPage.about": "À propos de ce show",
    "showPage.when": "Date",
    "showPage.venue": "Salle",
    "showPage.rider": "Rider",
    "showPage.riderValue": "{title}, v{version}",
    "showPage.dateOpen": "Date pas encore convenue",
    "showPage.venueNone": "Salle pas encore choisie",
    "showPage.makeOffer": "Faire une offre",
    "showPage.archived.past": "Ce show a eu lieu. Cette page en est l'archive : elle n'accepte plus d'offres.",
    "showPage.archived.agreed": "Ce show est confirmé. Cette page en est l'archive : elle n'accepte plus d'offres.",
    "showPage.repeat": "Répéter ce show",
    "showPage.loadFailed": "Impossible de lire ce show : {error}",
    "repeatShow.title": "Répéter ce show",
    "repeatShow.hint": "Un nouveau show avec la même salle et les mêmes artistes. L'original reste tel quel.",
    "repeatShow.whenWhere": "Nouvelle date",
    "repeatShow.rider.newer": "Le rider a une version plus récente (v{newest} ; joué en v{played})",
    "repeatShow.rider.quiet": "Le rider n'a pas changé depuis le show (v{played}).",
    "repeatShow.rider.newest": "Prendre la plus récente",
    "repeatShow.rider.keep": "Garder celle qui a été jouée",
    "repeatShow.rider.ask": "Demander un nouveau rider au groupe",
    "repeatShow.offers.title": "Offres",
    "repeatShow.offers.count": "Offres approuvées sur ce show : {n}",
    "repeatShow.offers.invite": "Réinviter la même entreprise",
    "repeatShow.offers.none": "Commencer sans offres",
    "repeatShow.create": "Créer le nouveau show",
    "repeatShow.creating": "Création du show…",
    "repeatShow.needAnswer": "Répondez aux questions ci-dessus pour continuer.",
    "repeatShow.done": "Le nouveau show est créé. Voici son lien pour les prestataires :",
    "repeatShow.failed": "Impossible de répéter le show : {error}",
    "emailLogin.title": "Connexion",
    "emailLogin.checking": "Vérification du lien…",
    "emailLogin.failed": "Ce lien a expiré ou a déjà été utilisé. Rouvrez la fiche technique et appuyez sur Envoyer : vos réponses sont gardées dans ce navigateur pendant 48 heures.",
    "emailLogin.noToken": "Ce lien est incomplet. Ouvrez-le à nouveau depuis l'e-mail.",
    "emailLogin.success": "Vous êtes connecté.",
    "emailLogin.offerFailed": "L'offre envoyée avec votre e-mail n'a pas pu être remise — la fiche technique a peut-être été retirée. Rouvrez-la et renvoyez l'offre.",
    "emailLogin.usedTitle": "Ce lien a déjà été utilisé",
    "emailLogin.usedBody": "Si vous avez envoyé une offre avec ce lien, elle est partie : elle se trouve dans le compte {email}. Nous enverrons un nouveau lien à la même adresse : il ouvre votre compte avec votre offre, et vous pourrez y définir un mot de passe.",
    "emailLogin.usedBodyNoEmail": "Si vous avez envoyé une offre avec ce lien, elle est partie. Saisissez votre e-mail et nous enverrons un nouveau lien vers votre compte.",
    "emailLogin.emailLabel": "Votre e-mail",
    "emailLogin.sendNew": "M'envoyer un nouveau lien",
    "emailLogin.signIn": "J'ai déjà un compte — me connecter",
    "emailLogin.newSent": "Nous avons envoyé un nouveau lien à {email}. Ouvrez-le : vous arriverez dans votre compte, à côté de votre offre.",
    "setPassword.title": "Définissez un mot de passe pour revenir plus tard",
    "setPassword.hint": "Votre compte a été créé par le lien de l'e-mail. Avec un mot de passe, vous pourrez l'ouvrir à tout moment et voir votre offre et la réponse de l'auteur.",
    "setPassword.label": "Nouveau mot de passe (au moins 8 caractères)",
    "setPassword.button": "Définir le mot de passe",
    "setPassword.tooShort": "Le mot de passe doit comporter au moins 8 caractères.",
    "setPassword.done": "Mot de passe défini. La prochaine fois, connectez-vous avec votre e-mail et ce mot de passe.",
    "setPassword.failed": "Impossible de définir le mot de passe. Réessayez.",
    "offer.validation.modelRequired": "Indiquez un modèle pour chaque réponse « Substitut » avant d'enregistrer.",
    "offer.validation.reasonRequired": "Indiquez une raison pour chaque réponse « N/D » avant d'enregistrer.",
    "offer.readOnlyNotice": "Cette offre est « {status} » et ne peut plus être modifiée.",
    "offer.table.answer": "Réponse",
    "offer.bandBringsNote": "Apporté par le groupe",
    "offer.gear.consumables": "Consommables — des kits confirmés",
    "offer.gear.cablesElsewhere": "Les câbles figurent dans leur propre section ci-dessous.",
    "offer.notConnectedNote": "Non connecté",
    "offer.answer.unanswered": "—",
    "offer.answer.ok": "OK",
    "offer.answer.markOk": "Confirmer : OK",
    "offer.answer.substitute": "Substitut",
    "offer.answer.bandCarried": "Apporté par le groupe",
    "offer.answer.na": "N/D",
    "offer.answer.venueProvided": "Fourni par la salle",
    "offer.answer.modelPlaceholder": "Modèle",
    "offer.alt.warehouseEmpty": "Rien chez vous dans cette rubrique",
    "offer.alt.searchCatalogue": "Chercher dans notre catalogue",
    "offer.answer.reasonPlaceholder": "Raison",

    "offer.answer.commentPlaceholder": "Commentaire",
    "offer.answer.short.ok": "OK",
    "offer.answer.short.substitute": "Alt",
    "offer.answer.short.bandCarried": "Groupe",
    "offer.answer.short.na": "N/D",
    "offer.answer.short.venueProvided": "Salle",
    "offer.collapseAll": "Tout replier",
    "offer.expandAll": "Tout déplier",
    "offer.patchShow": "Patch de ce show →",
    "offer.confirmedOf": "{done} sur {total} confirmés",
    "offer.summary.confirmed": "confirmé",
    "offer.summary.alternatives": "avec équivalent",
    "offer.summary.unavailable": "indisponible",
    "offer.summary.open": "sans réponse",
    "offer.summary.nothingYet": "Rien de répondu pour l'instant",
    "offer.allConfirmed": "Tout est confirmé",
    "offer.sectionToggle": "Ouvrir ou fermer cette section",
    "offer.plan.title": "Plan de scène",
    "offer.plan.io": "I/O",
    "offer.plan.ioShow": "Afficher les légendes des canaux et des mixes",
    "offer.plan.ioHide": "Masquer les légendes des canaux et des mixes",
    "offer.plan.open": "Touchez pour ouvrir en plein écran",
    "offer.plan.close": "Fermer",
    "offer.plan.zoomIn": "Zoom avant",
    "offer.plan.zoomOut": "Zoom arrière",
    "offer.plan.loading": "Dessin du plan de scène…",
    "offer.plan.failed": "Le plan de scène n'a pas pu être dessiné.",
    "offer.wire.checkPatch": "Vérifier le patch du show et les longueurs de câble →",
    "offer.wire.hint": "Ouvre l'éditeur de patch et le calcul des câbles",
    "offer.wire.longMic": "Câbles micro longs",
    "offer.wire.shortPatch": "Câbles de patch courts",
    "offer.detail.each": "Chacun des {n} — à quoi il sert",
    "offer.detail.kit": "kit {name}",
    "offer.detail.kitHide": "kit {name}",
    "offer.amp.summary": "{n} × LA4X amplified controller · {capacity} output channels · utilisés {used} · libres {free}",
    "offer.amp.capacity": "drive capacity par LA4X : 2 × 115XT HiQ ou 4 × SB18 (L-Acoustics)",
    "offer.amp.channels": "{n} can.",
    "offer.amp.twoWay": "2-way active (LF + HF)",
    "offer.amp.oneChannel": "1 output channel",
    "offer.amp.used": "{used}/{capacity} can.",
    // --- offer review mode (pages/offer-review-overlay.ts, PR3 — docs/V2_OFFER_TECH_APPROVAL.md) -----
    "offer.review.supplierCommentLabel": "Commentaire du prestataire",
    "offer.review.noComment": "Aucun commentaire.",
    "offer.review.yourRequestHeading": "Modifications que vous avez demandées",
    "offer.review.approvedNotice": "Cette offre a été approuvée. Plus aucune modification n'est possible.",
    "offer.review.approve": "Approuver",
    "offer.review.requestChanges": "Demander des modifications",
    "offer.review.requestChangesCommentLabel": "Que faut-il changer ?",
    "offer.review.requestChangesCommentPlaceholder": "Indiquez au prestataire ce qu'il faut corriger avant de renvoyer…",
    "offer.review.sendRequest": "Envoyer",
    "offer.review.approving": "Approbation…",
    "offer.review.approveFailed": "Impossible d'approuver : {error}",
    "offer.review.sending": "Envoi…",
    "offer.review.requestFailed": "Impossible d'envoyer : {error}",
    "offer.review.commentRequired": "Indiquez ce qui doit changer avant d'envoyer.",
    "offer.review.supplierNameUnknown": "Prestataire inconnu",
    "offer.review.historyHeading": "Historique",
    "offer.review.history.submitted": "Envoyé",
    "offer.review.history.changesRequested": "Modifications demandées",
    "offer.review.history.approved": "Approuvé",
    "offer.status.declined": "Refusée",
    "offer.review.decline": "Refuser",
    "offer.review.declinedNotice": "Cette offre a été refusée. Le fournisseur ne peut ni la modifier ni la renvoyer.",
    "offer.review.declineConfirm": "Refuser cette offre ? C'est définitif : le fournisseur sera prévenu et ne pourra pas la renvoyer.",
    "offer.review.declineReasonLabel": "Motif (facultatif)",
    "offer.review.declineReasonPlaceholder": "Le fournisseur le verra dans l'e-mail…",
    "offer.review.declineConfirmButton": "Refuser l'offre",
    "offer.review.declining": "Refus en cours…",
    "offer.review.declineFailed": "Impossible de refuser : {error}",
    "offer.review.history.declined": "Refusée",

    // --- /offers — the two-sided offer inbox (pages/offers-page.ts, PR3) -----------------------------
    "offers.pageTitle": "Offres",
    "offers.incomingHeading": "Offres sur mes fiches techniques",
    "offers.mineHeading": "Mes offres",
    "offers.incomingEmpty": "Personne n'a encore répondu à l'une de vos fiches publiées.",
    "offers.mineEmpty": "Vous n'avez pas encore fait d'offre sur une fiche technique.",
    "offers.table.rider": "Fiche technique",
    "offers.table.supplier": "Prestataire",
    "offers.table.supplierUnknown": "Prestataire inconnu",
    "offers.table.status": "Statut",
    "offers.table.round": "Tour",
    "offers.table.updated": "Mis à jour",
    "offers.status.loading": "Chargement…",
    "offers.status.loadFailed": "Impossible de charger les offres : {error}",

    // --- editor topbar (components/topbar.ts) -----------------------------------------------------------
    "topbar.loggedInAs": "connecté en tant que {email}",
    "topbar.adminBadge": "admin",
    "topbar.adminBadgeTitle": "Vous disposez des privilèges d'administrateur (ex. envoi d'objets 3D).",
    "topbar.unsavedRiderId": "(nouvelle fiche non enregistrée)",
    "topbar.save.title": "Enregistrer la fiche technique dans la base de données ({mod}+S)",
    "topbar.save.copyTitle":
        "Enregistrer comme ma propre copie — cette fiche appartient à quelqu'un d'autre, l'enregistrement créera une NOUVELLE fiche sur votre compte ({mod}+S)",
    "topbar.save.copyLabel": "Enregistrer comme ma propre copie",
    "topbar.saveAs.title": "Enregistrer sous… — enregistre le rider ouvert comme un NOUVEAU rider sous le nom de votre choix, sans toucher à l’original ({mod}+Shift+S)",
    "topbar.saveAs.label": "Enregistrer sous…",
    "topbar.attach.title": "Attacher un objet à un autre — activez le mode, puis cliquez sur l'objet parent",
    "topbar.attach.label": "Attacher l'objet",
    "topbar.attach.armedTitle": "Mode attachement actif — cliquez sur l'objet dans la scène 3D qui deviendra le parent (Échap pour annuler).",
    "topbar.attach.armedLabel": "Sélectionnez une cible…",
    "topbar.publish.label": "Publier la fiche",
    "topbar.publish.titleUnsaved": "Publier la fiche — enregistrez d'abord la fiche",
    "topbar.publish.titleNotMine": "Publier la fiche — seul le propriétaire peut publier cette fiche",
    "topbar.publish.titlePublished": "Publiée sous la v{version} — lien public : {url} (cliquez pour dépublier)",
    "topbar.publish.unpublishLabel": "Dépublier la fiche",
    "topbar.publish.titleDefault": "Publier la fiche — la rendre accessible via un lien public en lecture seule (pour QR code sur l'impression)",

    // --- editor page chrome (pages/editor-page.ts, EDITOR_PAGE_HTML template) ----------------------------
    "editor.riderIdTitle": "Identifiant de la fiche enregistrée (vide = nouvelle fiche)",
    "editor.menuToggleTitle": "Réduire/développer le menu",
    "editor.menuToggleLabel": "Menu",
    "editor.overflowMenuTitle": "Plus",
    "editor.rightPanelCollapseTitle": "Réduire le panneau",
    "editor.rightPanelExpandTitle": "Développer le panneau",
    "editor.rightPanelResizerTitle": "Faites glisser pour redimensionner le panneau (double-cliquez pour revenir à la largeur automatique)",
    "editor.undoTitle": "Annuler ({mod}+Z)",
    "editor.undoLabel": "Annuler",
    "editor.redoTitle": "Rétablir ({mod}+Shift+Z)",
    "editor.redoLabel": "Rétablir",
    "editor.rotateTitle":
        "Pivoter (R) — activez l'anneau de rotation et faites-le glisser. Par défaut, fait pivoter l'ensemble du set (objet + enfants) ; maintenez Alt pour pivoter l'objet seul.",
    "editor.rotateLabel": "Pivoter",
    "editor.attachArmedHint": "Cliquez sur l’objet auquel rattacher « {title} » : un podium, un pied · Échap pour annuler",
    "editor.modifierAltHint": "Alt — cet objet seulement",
    "editor.modifierAltStackHint": "Alt — toute la pile",
    "editor.modifierShiftHint": "Maj — cliquez sur des objets pour les grouper, relâchez pour terminer (ou relâchez sur un objet en glissant pour attacher)",
    "editor.uploadObjectTitleDisabled": "Envoyer un objet 3D — réservé aux admins",
    "editor.uploadObjectTitleAdmin": "Envoyer un objet 3D — créer un nouveau préréglage global à partir d'un fichier .glb",
    "editor.uploadObjectLabel": "Envoyer un objet 3D",
    "editor.deleteObjectLabel": "Supprimer l'objet sélectionné",
    "editor.deleteObjectTitle": "Supprimer l'objet sélectionné (Alt+Delete)",
    "editor.savePresetLabel": "Enregistrer comme préréglage",
    "editor.deleteSubtreeLabel": "Supprimer l'objet avec tous ses enfants",
    "editor.deleteSubtreeTitle": "Supprimer l'objet avec tous ses enfants (Delete)",
    "editor.selectMode.groupLabel": "Toute la pile",
    "editor.selectMode.groupTitle": "Sélection : toute la pile — déplacer, tourner et supprimer agissent sur le groupe (maintenir Alt pour un seul objet)",
    "editor.selectMode.itemLabel": "Un seul objet",
    "editor.selectMode.itemTitle": "Sélection : un seul objet — déplacer, tourner et supprimer agissent sur l’objet cliqué (maintenir Alt pour toute la pile)",
    "editor.deleteInGroupLabel": "Supprimer toute la pile",
    "editor.deleteInGroupTitle": "Supprimer toute la pile (Delete) — Alt : seulement l’objet cliqué",
    "editor.deleteInItemLabel": "Supprimer cet objet",
    "editor.deleteInItemTitle": "Supprimer l’objet cliqué (Delete) — Alt : toute la pile",
    "editor.deleteGroupConfirm.heading": "Supprimer toute la pile ?",
    "editor.deleteGroupConfirm.body": "« {title} » sera retiré de la scène avec tout ce qui est posé dessus. Objets : {n}.",
    "editor.deleteGroupConfirm.confirmLabel": "Supprimer la pile",
    "editor.showAnnotationsLabel": "Afficher/masquer les annotations",
    "editor.annotationOptionsTitle": "Options d'affichage des annotations",
    "editor.annotationContentAriaLabel": "Contenu des annotations",
    "editor.labelsPlaqueStrip": "LABELS",
    "editor.annotationCh": "CH",
    "editor.topViewTitle": "Vue de dessus",
    "editor.topViewLabel": "Vue de dessus",
    "editor.planViewTitle": "Plan",
    "editor.planViewLabel": "Plan",
    "editor.view3dLabel": "3D",
    "editor.view3dTitle": "Vue 3D",
    "editor.topViewShort": "Top",
    "editor.planViewShort": "Plan",
    "editor.viewSwitcherAriaLabel": "Vue",
    "editor.viewSwitcherOptionsTitle": "Options d'affichage",
    "editor.planViewLoading": "Tracé du plan…",
    "editor.planViewFailed": "Impossible de charger la bibliothèque de symboles.",
    "editor.status.planMoveRefused": "Cet objet ne peut pas être déplacé là.",
    "presetMenu.editMode": "Modifier",
    "presetMenu.editModeDone": "Terminé",
    "presetMenu.reorderFailed": "Impossible d'enregistrer l'ordre : {error}",
    "presetMenu.setDefaultOrder": "Définir comme ordre par défaut",
    "presetMenu.defaultOrderSaved": "Cet ordre est désormais celui par défaut pour ceux qui n'ont pas rangé le leur.",
    "editor.annotationSource": "Source",
    "editor.annotationMix": "MIX",
    "editor.annotationTitle": "Titre",
    "editor.annotationDesc": "Description",
    "editor.showGridLabel": "Afficher/masquer la grille de coordonnées",
    "editor.printLabel": "Imprimer la fiche technique",
    "editor.printTitle": "Exporter en PDF et imprimer",
    "editor.exitLabel": "Retour à Mes Fiches",
    "editor.riderOperationsAriaLabel": "Opérations sur la fiche technique",
    "editor.tabs.properties": "Properties",
    "editor.tabs.propertiesTitle": "Properties (E)",
    "editor.tabs.rider": "Fiche",
    "editor.tabs.inputList": "Input List",
    "editor.tabs.inputListTitle": "Input List (I)",
    "editor.tabs.outputList": "Output List",
    "editor.tabs.outputListTitle": "Output List (O)",
    "editor.tabs.gearList": "Gear List",
    "editor.riderTab.heading": "Fiche technique",
    "editor.riderTab.titleLabel": "Titre",
    "editor.riderTab.commentLabel": "Remarque",
    "editor.riderTab.stageWidthLabel": "Largeur (m)",
    "editor.riderTab.stageDepthLabel": "Profondeur (m)",
    "editor.riderTab.useBorderLabel": "Restreindre aux limites de la scène",
    "editor.riderTab.useBorderHint": "(désactivé = autorise les objets hors scène — régie, décor)",
    "editor.riderTab.contactLegend": "Contacts et groupe",
    "editor.riderTab.moduleRider": "Rider",
    "editor.riderTab.moduleStage": "Scène",
    "editor.riderTab.moduleBand": "Groupe",
    "editor.riderTab.moduleContact": "Contact",
    "editor.riderTab.contactHint": "(pré-rempli depuis votre profil ; enregistré avec cette fiche)",
    "editor.riderTab.bandNameLabel": "Nom du groupe / artiste",
    "editor.riderTab.bandLogoAlt": "Logo du groupe",
    "editor.riderTab.bandLogoFileLabel": "Fichier du logo",
    "editor.riderTab.removeLogo": "Supprimer le logo",
    "editor.riderTab.contactNameLabel": "Nom du contact",
    "editor.riderTab.contactPhoneLabel": "Téléphone du contact",
    "editor.riderTab.contactEmailLabel": "E-mail du contact",
    "editor.riderTab.saveDefaultsTitle":
        "Enregistrer ces coordonnées comme vos valeurs par défaut — elles pré-rempliront chaque nouvelle fiche créée",
    "editor.riderTab.saveDefaultsLabel": "Enregistrer comme mes valeurs par défaut",
    "editor.riderTab.riderTextLabel": "Texte de la fiche / Notes",
    "editor.riderTab.savedDefaultsStatus": "Enregistré comme valeurs par défaut.",
    "editor.riderTab.savedDefaultsFailed": "Impossible d'enregistrer les valeurs par défaut : {message}",
    "editor.listHint": "(fiche entière — glissez une ligne pour réordonner ; Ch = sa position. Cliquez sur une ligne pour sélectionner l'objet correspondant.)",
    "editor.reorderAriaLabel": "Réordonner",
    "editor.table.ch": "Ch",
    "editor.table.source": "Source",
    "editor.table.type": "Type",
    "editor.table.chName": "Nom voie",
    "editor.table.micDi": "Micro / DI",
    "editor.table.out": "OUT",
    "editor.table.comment": "Remarque",
    "editor.table.name": "Nom",
    "editor.table.category": "Catégorie",
    "editor.table.owner": "Propriétaire",
    "editor.table.weight": "Poids",
    "editor.table.count": "Qté",
    "editor.inputTab.heading": "Input List (Entrées)",
    "editor.inputTab.addButton": "Ajouter un canal d'entrée",
    "editor.outputTab.heading": "Output List (Sorties)",
    "editor.outputTab.addButton": "Ajouter une ligne de sortie",
    "editor.gearTab.heading": "Gear List (Matériel)",
    "editor.gearTab.hint": "(fiche entière — matériel de chaque objet. Cliquez sur une ligne pour sélectionner son objet.)",
    "editor.gearTab.addButton": "Ajouter un équipement",
    "editor.params.closeTitle": "Désélectionner / fermer",
    "editor.params.emptyHint": "Select an object on the stage to see its properties.",
    "editor.params.heading": "Objet sélectionné",
    "editor.params.nameLabel": "Nom du modèle",
    "editor.params.nameHint": "(clé de catalogue, lecture seule)",
    "editor.params.titleLabel": "Titre",
    "editor.params.titleToSourceLabel": "Appliquer le titre à la Source",
    "editor.params.commentLabel": "Remarque",
    "editor.params.parentLabel": "Parent",
    "editor.params.xLabel": "X",
    "editor.params.yLabel": "Y",
    "editor.params.zLabel": "Z",
    "editor.params.zTitle": "Hauteur au-dessus de la scène (m). Pour les objets décoratifs hors du plan de la scène, p. ex. un écran ou des rideaux.",
    "editor.params.turnLabel": "Angle de rotation°",
    "editor.params.lockPositionLabel": "Verrouiller la position",
    "editor.params.lockChildrenLabel": "Verrouiller les enfants",
    "editor.params.structureLabel": "Structure scénique",
    "editor.params.structureHint": "(d'autres objets peuvent reposer dessus)",
    "editor.params.heightLabel": "Hauteur, m",
    "editor.params.heightHint": "(hauteur de surface)",
    "editor.params.riserAcrossLabel": "Largeur, m",
    "editor.params.riserDeepLabel": "Profondeur, m",
    "editor.params.riserHeightsLabel": "Hauteurs rapides, m",
    "editor.params.riserResizeLabel": "Déplacer et redimensionner sur scène",
    "presetMenu.riserTooltipHint": "double-clic pour redimensionner",
    "editor.params.riserResizeDoneLabel": "Terminer",
    "editor.params.riserResizeHint": "Faites glisser l'objet pour le déplacer, les poignées pour le redimensionner. Maj = libre, Entrée ou Échap = terminer.",
    "editor.params.stageMoveLabel": "Déplacer sur scène",
    "editor.params.stageMoveHint": "Faites-le glisser sur la scène pour le déplacer. Maj = libre, Entrée ou Échap = terminer.",
    "editor.params.anchoredHint": "Ancré — double-clic pour déplacer ou redimensionner",
    "editor.params.anchoredMoveHint": "Ancré — double-clic pour déplacer",
    "editor.params.anchoredLockedHint": "Verrouillé — décochez « Verrouiller la position » pour le déplacer",
    "editor.params.snapToGridLabel": "Aimanter à la grille",
    "editor.params.snapNeedsGridHint": "L'aimantation ne fonctionne que si la grille est affichée (le bouton de grille de la barre du haut). Grille masquée, tout bouge librement.",
    "editor.params.snapToGridHint": "Les bords se calent sur la grille de ce dont l'objet est fait : plateau de 0,5 m pour le praticable, cabinet pour le mur LED, tronçon standard pour la structure. Désactivé : l'objet reste exactement où vous le posez.",
    "riser.handle.deep": "Faire glisser pour changer la profondeur",
    "riser.handle.across": "Faire glisser pour changer la largeur",
    "riser.handle.corner": "Faire glisser pour changer la largeur et la profondeur",
    "riser.handle.height": "Faire glisser pour changer la hauteur (pas de 0,2 m)",
    "riser.handle.dock": "Ajouter un praticable identique de ce côté",
    "riser.readout": "{across} × {deep} m · H {height} m",
    "stage.handle.deep": "Faire glisser pour changer la profondeur de la scène",
    "stage.handle.across": "Faire glisser pour changer la largeur de la scène",
    "stage.handle.corner": "Faire glisser pour changer la largeur et la profondeur de la scène",
    "stage.readout": "Scène {across} × {deep} m",
    "riser.status.docked": "Un praticable identique a été ajouté à côté de celui-ci.",
    "ledWall.handle.cols": "Faire glisser pour ajouter ou retirer des colonnes de caissons",
    "ledWall.handle.rows": "Faire glisser pour ajouter ou retirer des rangées de caissons",
    "construction.led.cabinetLabel": "Caisson, mm",
    "construction.led.cabinetCustom": "{w} × {h} (personnalisé)",
    "construction.led.colsLabel": "Colonnes",
    "construction.led.rowsLabel": "Rangées",
    "construction.led.pitchLabel": "Pas de pixel, mm",
    "construction.led.mountLabel": "Montage",
    "construction.led.mountGround": "Posé au sol",
    "construction.led.mountFlown": "Accroché",
    "construction.led.bottomLabel": "Bord inférieur, m",
    "construction.led.contentLabel": "Affichage",
    "construction.led.contentOff": "Éteint (noir)",
    "construction.led.contentGrid": "Grille des caissons",
    "construction.led.contentBars": "Mire de barres",
    "construction.led.contentLogo": "Carton logo",
    "construction.led.contentImage": "Image",
    "construction.led.contentMedia": "Média",
    "construction.led.chooseImage": "Choisir une image…",
    "construction.led.removeImage": "Retirer l'image",
    "construction.led.imageSet": "Image placée sur le mur LED ({w} × {h} px).",
    "construction.led.imageTooBig": "L'image reste trop lourde même réduite. Choisissez-en une plus petite.",
    "construction.led.imageUnreadable": "Ce fichier n'a pas pu être lu comme image.",
    "construction.led.imageRemoved": "Image retirée du mur LED.",
    "construction.led.mediaLabel": "Média",
    "construction.led.mediaLogo": "Logo SoundCheckBox",
    "construction.led.mediaMascot": "Mascotte",
    "construction.led.mediaIntro": "Intro",
    "construction.led.mediaIntroPlain": "Intro (sans argent)",
    "construction.led.mediaTutorial": "Vidéo tutoriel",
    "construction.led.mediaCustomUpload": "Importé : {name}",
    "construction.led.mediaCustomLink": "Lien : {host}",
    "construction.led.mediaUrlLabel": "Lien direct (https)",
    "construction.led.mediaUrlPlaceholder": "https://…/video.mp4",
    "construction.led.mediaUrlUse": "Afficher ce lien",
    "construction.led.mediaUpload": "Importer une vidéo ou une image…",
    "construction.led.mediaChecking": "Vérification du lien…",
    "construction.led.mediaUploading": "Importation…",
    "construction.led.mediaSet": "Le mur affiche maintenant ce choix.",
    "construction.led.mediaProblemEmpty": "Collez un lien vers un fichier vidéo ou image.",
    "construction.led.mediaProblemStreaming": "Les pages {site} ne peuvent pas être affichées sur le mur : {site} ne diffuse la vidéo que dans son propre lecteur. Utilisez un lien direct vers le fichier (.mp4 ou .webm) ou importez le fichier.",
    "construction.led.mediaProblemNotHttps": "Le lien doit commencer par https://",
    "construction.led.mediaProblemCredentials": "Retirez l'identifiant et le mot de passe du lien.",
    "construction.led.mediaProblemNotAddress": "Ce n'est pas une adresse web. Collez le lien complet, qui commence par https://",
    "construction.led.mediaProblemTooLong": "Ce lien est trop long.",
    "construction.led.mediaFailCors": "Ce site n'autorise pas l'affichage de sa vidéo sur d'autres pages.",
    "construction.led.mediaFailUnreachable": "L'adresse n'a pas répondu. Vérifiez le lien et votre connexion.",
    "construction.led.mediaFailHttp": "L'adresse a répondu par une erreur ({status}) : le fichier n'existe pas ou n'est pas public.",
    "construction.led.mediaFailNotMedia": "Cette adresse est une page web ou une liste de flux, pas un fichier vidéo ou image. Il faut un lien direct vers un fichier .mp4, .webm, .png, .jpg ou .gif.",
    "construction.led.mediaFailFormat": "Le navigateur n'a pas pu lire ce fichier. Utilisez une vidéo mp4 (H.264) ou webm, ou une image png, jpeg ou gif.",
    "construction.led.mediaUploadForbidden": "Seuls les administrateurs peuvent importer des médias.",
    "construction.led.mediaUploadType": "Seuls les fichiers mp4, webm, png, jpeg et gif peuvent être mis sur le mur.",
    "construction.led.mediaUploadEmpty": "Le fichier est vide.",
    "construction.led.mediaUploadTooBig": "Le fichier est trop volumineux : vidéos jusqu'à {video} Mo, images jusqu'à {image} Mo.",
    "construction.led.mediaUploadFailed": "L'importation a échoué. Réessayez.",
    "construction.led.derivedSize": "{size} m",
    "construction.led.derivedPx": "{px} px",
    "construction.led.derivedCount": "caissons : {count}",
    "construction.led.derivedLoad": "env. {kg} kg · env. {kw} kW max",
    "construction.truss.typeLabel": "Type",
    "construction.truss.typeLadder": "Échelle",
    "construction.truss.typeTriangle": "Triangulaire",
    "construction.truss.typeBox": "Carrée",
    "construction.truss.profileLabel": "Profil, mm",
    "construction.truss.lengthLabel": "Longueur, m",
    "construction.truss.trimLabel": "Hauteur (sous la poutre), m",
    "construction.truss.supportLabel": "Support",
    "construction.truss.supportFlown": "Accrochée (moteurs)",
    "construction.truss.supportFloor": "Au sol",
    "construction.truss.supportGoalpost": "Portique (deux tours)",
    "construction.truss.sections": "Éléments : {sections}",
    "construction.truss.towers": "tours 2 × {h} m",
    "construction.truss.longSpan": "grande portée — vérifiez l'abaque de charge",
    "editor.params.listsInputs": "Entrées",
    "editor.params.listsOutputs": "Sorties",
    "editor.params.listsGear": "Matériel",
    "editor.params.moduleObject": "Objet",
    "editor.params.modulePosition": "Position",
    "editor.params.listPosition": "position",
    "editor.params.listPositions": "positions",
    "editor.params.actionsLegend": "Actions sur l'objet",
    "editor.params.rotateHint":
        "Pivotez à l'aide de l'outil <strong>Pivoter</strong> dans la barre supérieure ou saisissez un angle exact ci-dessus.",
    "editor.params.addChildLabel": "Ajouter un enfant",
    "editor.params.deleteObjectLabel": "Supprimer l'objet",
    "editor.params.savePresetTitle": "Enregistrer cet objet et ses enfants comme un préréglage réutilisable",
    "editor.params.savePresetLabel": "Enregistrer comme préréglage",
    "editor.params.addInputLabel": "Ajouter une Entrée",
    "editor.params.addOutputLabel": "Ajouter une Sortie",
    "editor.params.linkOutputLabel": "Lier à une sortie",
    "editor.params.linkOutputTitle": "Lier cet objet à une ligne de sortie existante ou un numéro de canal",
    "editor.params.linkOutputDisabledTitle": "Aucune autre sortie disponible sur scène à lier",
    "editor.params.addGearLabel": "Ajouter du Matériel",
    "editor.presetCategoriesAriaLabel": "Catégories de préréglages",
    "editor.exitConfirm.title": "Quitter sans enregistrer ?",
    "editor.exitConfirm.body": "Vous avez des modifications non enregistrées sur cette fiche. Si vous quittez maintenant, elles seront perdues.",
    "editor.exitConfirm.saveAndLeave": "Enregistrer et quitter",
    "editor.exitConfirm.leaveWithoutSaving": "Quitter sans enregistrer",

    // --- editor page behaviour (pages/editor-page.ts, JS-built status messages) ---------------------------
    "editor.status.gluedToGroup": "Rejoint le groupe — {members} objets se déplacent désormais ensemble.",
    "editor.status.glueNoTarget": "Rien sous le point de dépôt — maintenez Maj sur un objet pour rejoindre son groupe.",
    "editor.status.glueFailed": "Impossible de rejoindre le groupe : {reason}",
    "editor.status.detached": "Retiré de son groupe — il se déplace seul désormais.",
    "editor.status.detachNotGrouped": "Cet objet n'est pas dans un groupe.",
    "editor.status.multiSelectCount": "{count} sélectionné(s) pour le groupe — relâchez Maj pour terminer.",
    "editor.status.grouped": "{count} objets groupés.",
    "editor.status.groupFailed": "Impossible de grouper : {reason}",
    "editor.status.attachCancelled": "Attachement annulé.",
    "editor.status.attachFailed": "Impossible d'attacher : {reason}",
    "editor.status.attached": "Attaché {source} à {target}.",
    "editor.status.undo": "Annulation.",
    "editor.status.redo": "Rétablissement.",
    "editor.status.attachArmed": "Mode attachement actif — cliquez sur l'objet qui deviendra le parent de {name} (Échap pour annuler).",
    "editor.status.savingCopy": "Enregistrement de votre copie…",
    "editor.status.savedAsCopy":
        "Enregistré en tant que NOUVELLE fiche — votre propre copie. L'original appartient à quelqu'un d'autre et reste inchangé.",
    "editor.status.saved": "Enregistré à {time}.",
    "editor.status.guestDraftRestored": "Votre travail précédent est là — il reste dans ce navigateur jusqu'à ce que vous l'enregistriez sur un compte.",
    "editor.status.guestDraftDiscarded":
      "Le travail enregistré dans ce navigateur n'a pas pu être ouvert en toute sécurité ; il a donc été abandonné plutôt que retenté. Démarrage avec un nouveau rider vide.",
    "editor.status.guestDraftNotSaved":
      "Votre navigateur n'a pas pu enregistrer vos modifications ici (le stockage est peut-être plein). Gardez cet onglet ouvert, ou connectez-vous et enregistrez pour conserver votre travail.",
    "editor.draft.offer": "Modifications non enregistrées du {time}.",
    "editor.draft.continue": "Restaurer ces modifications",
    "editor.draft.discard": "Garder ce qui est ouvert",
    "editor.draft.close": "Fermer — je déciderai plus tard",
    "editor.naming.offer": "Enregistré. Ce rider s'appelle encore « New rider » et le groupe n'a pas de nom — c'est par là que vous le chercherez ensuite.",
    "editor.naming.name": "Les nommer",
    "editor.naming.later": "Plus tard",
    "editor.draft.offerDiverged": "Vous avez des modifications non enregistrées du {time}, mais ce rider a été de nouveau enregistré sur le serveur depuis. Les restaurer par-dessus écraserait un travail plus récent.",
    "editor.draft.openAsCopy": "Ouvrir mes modifications comme copie",
    "editor.draft.useSaved": "Utiliser la version enregistrée",
    "editor.draft.restored": "Vos modifications non enregistrées sont de retour. Appuyez sur Enregistrer pour les garder.",
    "editor.draft.restoredAsCopy": "Vos modifications sont ouvertes comme un nouveau rider non enregistré. L'original enregistré n'a pas été touché. Appuyez sur Enregistrer pour garder la copie.",
    "editor.draft.notKept": "Votre navigateur n'a pas pu conserver une copie de secours de vos modifications (le stockage est peut-être plein). Appuyez sur Enregistrer pour ne pas les perdre si vous êtes déconnecté.",
    "editor.draft.crashed": "Les modifications non enregistrées conservées dans ce navigateur n'ont pas pu être ouvertes en toute sécurité ; elles ont été abandonnées plutôt que réessayées.",
    "editor.status.modelsUnavailable": "Certains modèles 3D n'ont pas pu être chargés — les objets s'affichent comme des boîtes étiquetées. Votre travail est intact.",
    "editor.status.presetsUnavailable": "Le catalogue d'objets n'a pas pu être chargé — vous pouvez continuer à dessiner, mais le menu des presets est vide. Rechargez pour réessayer.",
    "editor.status.guestSaveNeedsAccount": "Connectez-vous ou créez un compte pour conserver ce rider — votre travail est en sécurité et vous suit.",
    "editor.status.guestSaveFailed": "Impossible d'enregistrer sur votre compte : {message}. Votre travail est toujours là — appuyez sur Enregistrer pour réessayer.",
    "editor.status.saveFailed": "Échec de l'enregistrement : {message}",
    "editor.saveAs.heading": "Enregistrer comme nouveau rider",
    "editor.saveAs.nameLabel": "Nom",
    "editor.saveAs.confirm": "Enregistrer une copie",
    "editor.saveAs.hint": "Le rider ouvert reste tel quel — ceci enregistre une copie distincte sous le nom que vous indiquez.",
    "editor.saveAs.untitled": "Rider sans titre",
    "editor.status.savedAsNamedCopy": '« {title} » enregistré comme nouveau rider. Le rider ouvert est inchangé.',
    "editor.status.unpublished": "Dépubliée — tous les liens publics pour cette fiche ne fonctionnent plus.",
    "editor.status.published": "Publiée sous la v{version} — lien public : {url}",
    "editor.status.publishedNoPicture": "Publié, mais l'image du plan n'a pas pu être créée — republiez sans quitter cet onglet et le lecteur verra la scène pendant le chargement des modèles.",
    "editor.status.copiedSuffix": " (copié dans le presse-papiers)",
    "editor.status.publishFailed": "Échec de la publication : {message}",
    "publishProgress.publishing": "Publication du rider…",
    "publishProgress.drawing": "Publié en v{version}. Dessin du plan de scène pour la page publique…",
    "publishProgress.done": "Publié en v{version} — la page publique est prête, plan de scène compris.",
    "publishProgress.published": "Publié en v{version} — la page publique est prête.",
    "publishProgress.noPicture": "Publié en v{version}, mais le plan de scène n’a pas pu être dessiné. Les lecteurs verront la scène une fois ses modèles chargés. Publiez à nouveau sans quitter cet onglet pour ajouter l’image.",
    "publishProgress.failed": "Échec de la publication : {message}",
    "publishProgress.open": "Ouvrir la page publique",
    "editor.status.ioKeptNote": " Ses voies ont été conservées sous forme de lignes réservées vides.",
    "editor.status.ioOptimizedNote": " Ses voies ont été supprimées et la numérotation réajustée.",
    "editor.status.deletedWithChildren": "{title} supprimé ; {count} objet(s) enfant(s) conservé(s) et réattribué(s) sur place.",
    "editor.status.deleted": "{title} supprimé.",
    "editor.status.deletedSubtree": "{title} et tous ses objets enfants ont été supprimés.",
    "editor.status.rotated": "Pivoté {name} de {deg}° ({mode}).",
    "editor.status.rotateModeObject": "objet seul ; enfants maintenus",
    "editor.status.rotateModeSet": "ensemble complet",
    "editor.status.moved": "Déplacé {name} en X {x}, Y {y}.",
    "editor.status.dropMissed": "Dépose hors scène — essayez de déposer plus près du plateau.",
    "editor.status.loadingRider": "Chargement de la fiche…",
    "editor.status.loaded": "« {title} » chargée — {count} objets sur la scène.",
    "editor.status.loadedNotMineSuffix":
        " Cette fiche appartient à quelqu'un d'autre — vous pouvez l'éditer librement, mais l'enregistrement créera VOTRE propre copie.",
    "editor.status.loadFailed": "Échec du chargement : {message}",
    "editor.status.newRider": "Nouvelle fiche technique (non enregistrée).",

    // --- object-params panel behaviour (components/object-params-panel.ts) -------------------------------
    "objectParams.status.reparentFailed": "Impossible de changer de parent : {reason}",
    "objectParams.status.moved": "Déplacé {name} sous {location}.",
    "objectParams.status.locationNewParent": "un nouveau parent",
    "objectParams.status.locationTopLevel": "le premier niveau",
    "objectParams.status.lockedChildren": "Position verrouillée pour tous les descendants de {name}.",
    "objectParams.status.reseated": "Repositionné {n} objet(s) posé(s) sur {name}.",
    "objectParams.status.addedInput": "Ajouté un canal d'entrée à {name}.",
    "objectParams.status.addedOutput": "Ajouté une ligne de sortie à {name}.",
    "objectParams.status.addedGear": "Ajouté un équipement à {name}.",
    "objectParams.status.addedChild": "{childName} ajouté à {name}.",
    "objectParams.status.removed": "{name} supprimé. Les canaux des objets restants sont inchangés.",
    "objectParams.noParentOption": "(aucun parent — premier niveau)",

    // --- preset menu (components/preset-menu.ts) ----------------------------------------------------------
    "presetMenu.loading": "Chargement des préréglages…",
    "presetMenu.loadFailed": "Impossible de charger le catalogue d'objets. Rechargez la page pour réessayer.",
    "presetMenu.countSuffix": "({count})",
    "presetMenu.searchPlaceholder": "Rechercher dans le catalogue",
    "presetMenu.searchNoMatch": "Aucun résultat pour « {q} »",
    "presetMenu.noMatch": "Aucun préréglage ne correspond à ce filtre.",
    "presetMenu.onlyMine": "Seulement les miens",
    "presetMenu.added": 'Préréglage "{name}" ajouté sur la scène.',
    "presetMenu.removeFromCategory": "Retirer de {category}",
    "presetMenu.removedFromCategory": "« {name} » retiré de {category}. Le préréglage lui-même est intact.",
    "presetMenu.removeFromCategoryFailed": "Impossible de retirer « {name} » de {category} : {message}",
    "presetMenu.deleteLastCategoryPrompt": "Sa seule catégorie. La suppression retire le préréglage définitivement, sans retour possible. Supprimer ?",
    "presetMenu.deleteAlsoFromPrompt": "Supprime le préréglage définitivement — il disparaîtra aussi de {others}, sans retour possible. Supprimer ?",
    "presetMenu.deleting": 'Suppression du préréglage "{name}"…',
    "presetMenu.deleted": 'Préréglage "{name}" supprimé.',
    "presetMenu.deleteFailed": 'Échec de la suppression du préréglage "{name}" : {message}',
    "presetMenu.uncategorizedCategory": "Sans catégorie",
    "presetMenu.chooseCategoryPlaceholder": "— assigner une catégorie —",
    "presetMenu.assignedCategory": 'Préréglage "{name}" assigné à la catégorie "{category}".',
    "presetMenu.assignCategoryFailed": 'Impossible d\'assigner la catégorie au préréglage "{name}" : {message}',

    // --- Patch Editor (components/patch-editor-dialog.ts) — SCRUM-241 ---------------------------------------
    "patchEditor.title": "Éditeur de Patch & Câblage Scène",
    "patchEditor.subtitle": "Routage physique pour {count} canaux audio via boîtiers de scène",
    "patchEditor.stageBoxesHeading": "Boîtiers Numériques & Racks",
    "patchEditor.routingTableHeading": "Tableau de Routage des Canaux",
    "patchEditor.colChannel": "Canal",
    "patchEditor.colSource": "Source",
    "patchEditor.colSubSnake": "Sub-Snake / Drop Box",
    "patchEditor.colSocket": "Prise",
    "patchEditor.colStageBox": "StageBox",
    "patchEditor.colBoxInput": "Entrée Rack",
    "patchEditor.colCableLength": "Câble",
    "patchEditor.cableSummaryHeading": "Liste des Câbles (Spécification pour Flight Case)",
    "patchEditor.printSheet": "Imprimer la Feuille de Patch",
    "patchEditor.close": "Fermer",
    "patchEditor.done": "Terminé",
    "topbar.patchEditor": "Patch",
    "topbar.patchEditor.disabledTooltip": "Éditeur de Patch — Disponible en mode Show / Tournée lorsqu'un rider est associé à une salle avec des boîtiers et consoles.",
    "topbar.patchEditor.activeTooltip": "Ouvrir l'Éditeur de Patch (P)",

    // --- I/O keep-on-delete dialog (components/io-delete-dialog.ts) ----------------------------------------
    "ioDelete.heading": "Conserver la numérotation des voies ou réajuster la liste ?",
    "channelOverflow.heading": "Pas assez de canaux libres",
    "channelOverflow.summary": "« {preset} » demande {need} canaux d'entrée. {free} sont libres ({slots}) — il en manque {short}.",
    "channelOverflow.movingLine": "CH {ch} — {source} / {name}",
    "channelOverflow.movingMore": "{lines} … et {n} de plus",
    "channelOverflow.nothingToMove": "Rien au-dessus des canaux libres — le décalage ne déplace rien.",
    "channelOverflow.appendLabel": "Ajouter à la fin (CH {channels})",
    "channelOverflow.shiftLabel": "Décaler à partir de CH {from} de {n}",
    "channelOverflow.cancelled": "« {name} » n'a pas été ajouté — vous avez annulé la question des canaux.",
    "ioDelete.optimizeLabel": "Optimiser les voies",
    "ioDelete.keepLabel": "Conserver en l'état",
    "ioDelete.rememberLabel": "Mémoriser ce choix (ne plus demander)",
    "ioDelete.inputChannelSingular": "{n} voie d'entrée",
    "ioDelete.inputChannelPlural": "{n} voies d'entrée",
    "ioDelete.outputChannelSingular": "{n} circuit de sortie",
    "ioDelete.outputChannelPlural": "{n} circuits de sortie",
    "ioDelete.summary": "L'objet que vous supprimez contient {parts}.",

    // --- row-edit dialog (components/row-edit-dialog.ts) — docs/V2_IO_KEEP_ON_DELETE.md §3c --------------
    "rowEdit.headingInput": "Modifier le canal d'entrée",
    "rowEdit.headingOutput": "Modifier la ligne de sortie",
    "rowEdit.sourceLabel": "Source",
    "rowEdit.chNameLabel": "Nom du canal",
    "rowEdit.typeLabel": "Type",
    "rowEdit.commentLabel": "Commentaire",
    "rowEdit.lockedLabel": "Verrouillé",
    "rowEdit.chHashLabel": "Ch #",
    "rowEdit.lockedHint":
      "Le canal d'une ligne verrouillée ne peut pas changer. Décochez Verrouillé pour la déplacer, puis enregistrez — déverrouillage et déplacement se font ensemble, en un seul enregistrement.",
    "rowEdit.lockedHintInput": "Le numéro d'un canal verrouillé ne peut pas changer. Décochez Verrouillé pour le déplacer, puis enregistrez — déverrouillage et déplacement se font ensemble, en un seul enregistrement.",
    "rowEdit.linkedLabel": "Également connectés (mix partagé)",
    "rowEdit.linkedHint": "Autres retours alimentés par ce même mix — p. ex. deux wedges sur un départ. Ils afficheront aussi le numéro de canal de cette ligne sur le plan de scène.",
    "rowEdit.linkedEmpty": "Aucun autre retour sur la scène pour l'instant.",
    "rowEdit.saveLabel": "Enregistrer",
    "rowEdit.deleteChannel": "Supprimer le canal",
    "rowEdit.deleteLine": "Supprimer la ligne",
    "rowEdit.errorInvalidChannel": "Le canal doit être un nombre supérieur ou égal à 1.",
    "rowEdit.errorChannelTaken": "Le canal {channel} est déjà utilisé par une autre ligne — choisissez un numéro libre.",
    "rowEdit.errorLineGone": "Cette ligne n'existe plus — fermez cette fenêtre et réessayez.",
    "rowEdit.errorChannelGone": "Ce canal n'existe plus — fermez cette fenêtre et réessayez.",

    // --- link to existing output dialog (components/link-output-dialog.ts) ------------------
    "linkOutput.heading": "Lier à une sortie existante",
    "linkOutput.currentlyLinkedLabel": "Sorties actuellement liées :",
    "linkOutput.selectOutputLabel": "Sélectionner une ligne de sortie :",
    "linkOutput.chooseOutputOption": "Choisir une sortie existante...",
    "linkOutput.manualOption": "Saisir manuellement le numéro de canal...",
    "linkOutput.manualChannelLabel": "Numéro de canal :",
    "linkOutput.submitLabel": "Lier",
    "linkOutput.unlinkButtonTitle": "Délier cette sortie",
    "linkOutput.ownerSuffix": "propriétaire de cette sortie",
    "linkOutput.alreadyLinkedSuffix": "déjà lié",
    "linkOutput.errorSelectChannel": "Veuillez sélectionner une sortie ou saisir un numéro de canal valide.",
    "linkOutput.error.invalidChannel": "Saisissez un numéro de canal égal ou supérieur à 1.",
    "linkOutput.error.nodeNotFound": "Cet objet n'est plus sur le plateau. Fermez cette fenêtre et sélectionnez-le à nouveau.",
    "linkOutput.error.alreadyOwner": "Cette sortie appartient déjà à l'objet — il n'y a rien à relier.",
    "linkOutput.error.channelNotFound": "Aucune sortie sur ce canal pour l'instant. Créez-la d'abord avec « Add new Output ».",
    "linkOutput.errorGeneric": "Impossible de lier à la sortie.",
    "linkOutput.status.linked": "{name} lié à la sortie {channel}",
    "linkOutput.status.unlinked": "{name} délié de la sortie {channel}",

    // --- save-preset dialog (components/save-preset-dialog.ts) ---------------------------------------------
    "savePreset.heading": "Enregistrer comme préréglage",
    "savePreset.nameLabel": "Nom du préréglage",
    "savePreset.categoryLabel": "Catégorie",
    "savePreset.categoryRequiredHint": "(requis)",
    "savePreset.saveAsLabel": "Enregistrer sous",
    "savePreset.iconLegend": "Icône",
    "savePreset.renderingHint": "Rendu de l'image de l'objet…",
    "savePreset.useRenderedIcon": "Utiliser l'instantané 3D",
    "savePreset.useOriginalIcon": "Utiliser l'icône originale du preset",
    "savePreset.clearUploaded": "Effacer le fichier envoyé",
    "savePreset.submitLabel": "Enregistrer le préréglage",
    "savePreset.usingUploadedImage": "Utilisation de votre image envoyée.",
    "savePreset.usingOriginalPresetIcon": "Utilisation de l'image originale du preset.",
    "savePreset.autoGenerated": "Généré automatiquement depuis le modèle 3D — envoyez un fichier pour le remplacer.",
    "savePreset.iconPreviewAlt": "Aperçu de l'icône du préréglage",
    "savePreset.chooseCategoryOption": "— choisissez une catégorie —",
    "savePreset.newPresetOption": "Nouveau préréglage",
    "savePreset.overwriteOption": "Remplacer : {title}",
    "savePreset.sharedLibrarySuffix": " (bibliothèque partagée)",
    "savePreset.confirmOverwriteButton": "Oui, remplacer",
    "savePreset.backToEdit": "Retour",
    "savePreset.overwriteWarning":
      "Le remplacement écrase « {title} » — chaque future insertion utilisera votre nouvelle version. Les riders qui l'ont déjà utilisé conservent leur propre copie datant de leur ajout et ne changeront pas.",
    "savePreset.overwriteWarningSharedNote": "Ce préréglage fait partie de la bibliothèque partagée — votre modification sera visible par tous.",
    "savePreset.subject": "{title} — {count} objet(s), enregistré avec toute sa arborescence.",
    "savePreset.renderFailed": "Impossible de générer une icône — le préréglage sera enregistré sans icône.",
    "savePreset.errorNodeGone": "L'objet sélectionné n'existe plus — fermez cette fenêtre et sélectionnez à nouveau un objet.",
    "savePreset.errorNoCategory": "Choisissez une catégorie — un préréglage sans catégorie n'apparaîtra pas dans le menu.",
    "savePreset.errorStaleTarget": "Vous ne pouvez plus remplacer ce préréglage — réouvrez la fenêtre et réessayez.",
    "savePreset.updated": 'Préréglage "{title}" (id {id}) mis à jour — disponible immédiatement.',
    "savePreset.saved": 'Préréglage "{title}" (id {id}) enregistré — disponible dans le menu.',
    "savePreset.saveFailed": "Impossible d'enregistrer le préréglage : {message}",
    "savePreset.imageReadFailed": "Impossible de lire cette image : {message}",

    // --- Assistant IA (components/ai-assistant-dialog.ts) ---
    "ai.buttonTitle": "Assistant IA — décrivez une modification avec des mots",
    "ai.buttonLabel": "IA",
    "ai.heading": "Assistant IA",
    "ai.subheading": "Décrivez une modification de votre scène en langage courant. Vous vérifierez chaque changement avant son application.",
    "ai.promptLabel": "Votre demande",
    "ai.promptPlaceholder": "ex. ajoute une batterie au centre en fond de scène et un ampli basse à sa gauche",
    "ai.generate": "Générer",
    "ai.record": "Parler",
    "ai.recordTitle": "Dictez votre demande (jusqu'à 20 s)",
    "ai.recording": "Enregistrement… appuyez sur le micro pour arrêter.",
    "ai.stopRecording": "Arrêter l'enregistrement",
    "ai.micUnsupported": "La saisie vocale n'est pas prise en charge par ce navigateur.",
    "ai.micDenied": "L'accès au microphone a été refusé — autorisez-le dans le navigateur pour utiliser la voix.",
    "ai.micEmpty": "Aucun audio enregistré — réessayez.",
    "ai.generating": "Réflexion…",
    "ai.previewHeading": "Modifications proposées",
    "ai.apply": "Appliquer les modifications",
    "ai.discard": "Annuler",
    "ai.emptyPrompt": "Écrivez d'abord ce que vous souhaitez modifier.",
    "ai.noChanges": "L'assistant n'a proposé aucune modification. Essayez de reformuler votre demande.",
    "ai.notConfigured": "L'assistant IA n'est pas encore activé sur ce serveur.",
    "ai.capReached": "Vous avez épuisé votre quota IA gratuit. Ajoutez votre propre clé Gemini dans votre profil pour continuer à utiliser l'assistant.",
    "ai.providerError": "Le service IA est temporairement indisponible. Réessayez dans un instant.",
    "ai.invalid": "L'assistant n'a pas pu produire une modification valide pour ce rider. Essayez de reformuler.",
    "ai.invalidReference": "L'assistant a fait référence à un objet ou un preset absent de cette scène. Nommez-le autrement ou ajoutez-le d'abord.",
    "ai.someSkipped": "{count} modification(s) n'ont pas pu être appliquées (elles référençaient un élément absent de la scène ou ne tenaient pas) et ont été ignorées :",
    "ai.op.add": "Ajouter un objet",
    "ai.op.move": "Déplacer un objet",
    "ai.op.rotate": "Faire pivoter un objet",
    "ai.op.delete": "Supprimer un objet",
    "ai.op.settings": "Modifier les paramètres de la scène",
    "ai.op.unknown": "Une opération",
    "ai.failed": "Impossible de joindre l'assistant IA. Vérifiez votre connexion et réessayez.",
    "ai.applied": "{count} modification(s) de l'assistant appliquée(s).",
    "ai.diff.addPreset": "Ajouter {preset} à ({x}, {y}) m",
    "ai.diff.moveObject": "Déplacer {object} vers ({x}, {y}) m",
    "ai.diff.rotateObject": "Faire pivoter {object} de {deg}°",
    "ai.diff.deleteObject": "Supprimer {object}",
    "ai.diff.deleteTree": "Supprimer {object} et ses {count} objet(s) enfant(s)",
    "ai.diff.setRiderMeta": "Modifier les paramètres du rider : {fields}",
    "ai.diff.metaField.title": "titre",
    "ai.diff.metaField.comment": "commentaire",
    "ai.diff.metaField.width": "largeur de scène",
    "ai.diff.metaField.length": "profondeur de scène",

    // --- upload-object dialog (components/upload-object-dialog.ts) -----------------------------------------
    "uploadObject.heading": "Envoyer un objet 3D",
    "uploadObject.hint": "Réservé aux administrateurs. Crée un nouveau préréglage global à partir d'un fichier .glb.",
    "uploadObject.nameLabel": "Nom",
    "uploadObject.modelLabel": "Fichier de modèle (.glb)",
    "uploadObject.categoryLabel": "Catégorie",
    "uploadObject.iconLabel": "Icône (optionnelle)",
    "uploadObject.heightLabel": "Hauteur, m",
    "uploadObject.structureLabel": "Est une structure scénique (podium/praticable pouvant supporter d'autres objets)",
    "uploadObject.submitLabel": "Envoyer et créer le préréglage",
    "uploadObject.errorNameRequired": "Le nom est obligatoire.",
    "uploadObject.errorModelRequired": "Sélectionnez un fichier de modèle .glb.",
    "uploadObject.errorNotGlb": "Le modèle doit être au format .glb.",
    "uploadObject.errorNoCategory": "Choisissez une catégorie.",
    "uploadObject.uploading": "Envoi en cours…",
    "uploadObject.uploaded": '"{name}" envoyé — disponible dans la bibliothèque de préréglages.',

    // --- list tables (components/list-tables.ts, editor/dom-utils.ts) --------------------------------------
    "dom.deleteLineTitle": "Supprimer cette ligne",
    "dom.editLineTitle": "Modifier cette ligne",
    "dom.editChannelTitle": "Modifier ce canal",
    "dom.deleteChannelTitle": "Supprimer ce canal",
    "listTables.unconnectedRowTitle":
        "Non connecté — l'objet de cette voie a été supprimé ; le numéro est maintenu réservé. Supprimez cette ligne pour le libérer.",
    "listTables.unconnectedChannelTitle": "Non connecté — l'objet de cette voie a été supprimé ; le numéro est maintenu réservé. Supprimez-le pour libérer le numéro.",
    "listTables.phantomTitle": "Alimentation Fantôme (+48V) — cliquez pour basculer",
    "listTables.dragHandleTitle":
        "Glissez pour réordonner — la position détermine le numéro de voie. Ctrl/Cmd ou Maj-clic pour déplacer plusieurs lignes ensemble.",
    "listTables.clearSelectionTitle": "Cliquez pour effacer la sélection",
    "listTables.columnGripTitle": "{name} — glissez le bord pour redimensionner ; double-clic sur le bord pour ajuster",
    "listTables.panelGripTitle": "Faites glisser pour redimensionner le panneau — le même bord que celui du panneau",
    "listTables.noneOption": "(aucun)",
    "listTables.categoryFromCatalogue": "Le catalogue dit ce que c'est. Pour changer, changez le modèle, pas l'étiquette.",
    "listTables.categoryStale": "Cette ligne pointe vers la rubrique {ref} du catalogue, qui n'existe plus. Choisissez à nouveau le modèle.",
    "listTables.gearUngrouped": "Hors ensemble",
    "listTables.selectionBadge": "{count} sélectionnés — glissez n'importe lequel pour déplacer le groupe",
    "listTables.linkLabel": "Lier",
    "listTables.unlinkLabel": "Délier",
    "listTables.pickModeLabel": "Sélection multiple",
    "listTables.linkTitle": "Lier les deux lignes sélectionnées en paire stéréo",
    "listTables.unlinkTitle": "Délier la paire sélectionnée (les noms restent tels quels)",
    "listTables.linkNeedTwo": "Sélectionnez deux lignes voisines pour les lier en paire stéréo",
    "listTables.linkNotAdjacent": "Seules des lignes voisines peuvent être liées (canal N et N+1)",
    "listTables.linkAlreadyPaired": "L'une de ces lignes est déjà dans une paire : déliez-la d'abord",
    "listTables.unlinkNothing": "Sélectionnez une paire liée pour la délier",
    "listTables.pairMarkTitle": "Paire stéréo liée — canaux {a} et {b}",
    "ioDelete.pairSummary": "Cette ligne est une moitié de paire stéréo. L'autre moitié reste et devient une ligne simple (mono).",
    "listTables.powerApprox": '≈{watts} W par unité — correspondance approximative du catalogue ("{name}"), la puissance réelle peut varier.',
    "listTables.powerExact": "{watts} W par unité, selon le catalogue de matériel : {name}.",
    "listTables.powerLoading": "Chargement des données de puissance électrique…",
    "listTables.powerError": "Catalogue de matériel indisponible — puissance totale non calculée.",
    "listTables.notFoundInCatalog": "Non trouvé dans le catalogue : {names}",

    // --- power summary (editor/power.ts — shared by the Gear tab AND the printed rider) --------------------
    "power.noGearItems": "Aucun équipement pour l'instant.",
    "power.totalLine": "Scène / Régie : {backline} W · Système de diffusion (PA) : {pa} W",
    "power.fromLine": "— calculé pour {resolved} sur {items} éléments.",
    "picker.catalogLoading": "Chargement du catalogue…",
    "picker.samePurpose": "même usage",
    "picker.family": "apparenté",
    "picker.startTyping": "Tapez un modèle ou un fabricant",
    "picker.keepTyped": "Garder «{text}» tel quel",
    "picker.classAnswers": "Modèles qui répondent à «{name}»",
    "gear.notInCatalog": "pas dans le catalogue",
    "power.itemSingular": "{n} élément",
    "power.itemPlural": "{n} éléments",
    "power.zeroPowerSingular": "{items} ne consomme aucune énergie secteur.",
    "power.zeroPowerPlural": "{items} ne consomment aucune énergie secteur.",
    "power.unresolvedLine": "{items} non trouvé(s) dans le catalogue (non comptabilisé(s)).",

    // --- print document (editor/print-rider.ts) -------------------------------------------------------------
    "printRider.noInputChannels": "Aucune voie d'entrée.",
    "printRider.noOutputChannels": "Aucun circuit de sortie.",
    "printRider.noGearItems": "Aucun matériel.",
    "printRider.headers.ch": "Ch",
    "printRider.headers.v48": "48V",
    "printRider.headers.source": "Source",
    "printRider.headers.micDi": "Micro / DI",
    "printRider.headers.type": "Type",
    "printRider.headers.owner": "Propriétaire",
    "printRider.headers.comment": "Remarque",
    "printRider.headers.send": "Envoi",
    "printRider.headers.category": "Catégorie",
    "printRider.headers.name": "Nom",
    "printRider.headers.count": "Qté",
    "printRider.headers.belongsTo": "Rattaché à",
    "printRider.headers.offer": "OFFER",
    "printRider.gear.bandBrings": "Apporté par le groupe — ne pas commander",
    "printRider.gear.allCables": "Tous les câbles nécessaires au raccordement du matériel indiqué dans ce rider",
    "printRider.gear.bandNote": "Ce matériel sera sur scène : le groupe l'apporte lui-même. Rien à fournir ici.",
    "printRider.gear.ungrouped": "Hors ensemble",
    "printRider.gear.noCategory": "Sans catégorie",
    "gearList.section.pa": "Diffusion (P.A.)",
    "gearList.section.amplification": "Amplification",
    "gearList.section.mixing": "Système de mixage",
    "gearList.section.backline": "Backline",
    "gearList.section.monitoring": "Retours",
    "gearList.section.instruments": "Instruments",
    "gearList.section.mics": "Micros et DI",
    "gearList.section.stands": "Pieds",
    "gearList.section.stage": "Structures de scène",
    "gearList.section.power": "Distribution électrique",
    "gearList.section.wire": "Câbles",
    "gearList.section.consumables": "Consommables",
    "gearList.standFor.microphone": "Pieds de micro",
    "gearList.standFor.guitar-bass": "Stands guitare et basse",
    "gearList.standFor.keyboard": "Stands clavier",
    "gearList.standFor.wind": "Stands pour instruments à vent",
    "gearList.standFor.drums-percussion": "Stands batterie et percussions",
    "gearList.standFor.speaker": "Pieds d'enceinte",
    "gearList.standFor.sheet-music-other": "Pupitres et autres",
    "gearList.drumKit": "Batterie",
    "gearList.otherInstruments": "Autres instruments",
    "gearList.drumKitMics": "Micros de la batterie",
    "gearList.otherMics": "Autres micros et DI",
    "publicViewer.gear.eachFor": "Chacun des {n} — à quoi il sert",
    "gearList.bandBlock": "Apporté par le groupe",
    "gearList.where.standalone": "Position isolée",
    "gearList.where.standaloneMany": "{count} positions isolées",
    "gearList.where.acrossStage": "{count} emplacements sur scène",
    "gearList.alsoUnder": "aussi dans {section} : {count}",
    "gearList.derivedFrom": "de la liste d'entrées, voie {channels} · {source} — se modifie dans la liste d'entrées",
    "gearList.groupCount": "{lines} lignes · {items} pcs",
    "gearList.groupCountOne": "1 ligne · {items} pcs",
    "gearList.unstated.pa": "Non précisé dans cette fiche.",
    "gearList.unstated.mixing": "Non précisé dans cette fiche — calculé d'après ses listes d'entrées et de sorties.",
    "gearList.unstated.band": "Le groupe apporte le sien — voir en fin de liste.",
    "gearList.mixing.need": "Patch d'entrées : {inputs}. Patch de sorties : {outputs}.",
    "gearList.mixing.inputs.one": "{n} entrée",
    "gearList.mixing.inputs.few": "{n} entrées",
    "gearList.mixing.inputs.many": "{n} entrées",
    "gearList.mixing.inputs.other": "{n} entrées",
    "gearList.mixing.outputs.one": "{n} sortie",
    "gearList.mixing.outputs.few": "{n} sorties",
    "gearList.mixing.outputs.many": "{n} sorties",
    "gearList.mixing.outputs.other": "{n} sorties",
    "gearList.mixing.modelUnstated": "Modèle non précisé.",
    "gearList.mixing.modelUnstatedBare": "modèle non précisé",
    "gearList.mixing.requirement": "Système de mixage d'une capacité d'au moins {inputs} / {outputs} : {picks}.",
    "gearList.mixing.monitorRequirement": "Console de retours d'une capacité d'au moins {inputs} / {outputs} : {picks}.",
    "gearList.mixing.pickBrand": "n'importe quelle console {brand}",
    "mixing.line.title": "Système de mixage",
    "mixing.monitorDesk.title": "Console de retours",
    "mixing.monitorConsole.title": "Console de retours",
    "mixing.monitor.fromFoh": "Depuis la FOH",
    "mixing.monitor.separate": "Console séparée",
    "mixing.need.short": "≥{inputs} ent / {outputs} sor",
    "mixing.notSpecified": "Modèle non précisé",
    "mixing.line.tooltip": "{title} : {picks} — cliquer pour modifier",
    "mixing.dialog.need": "Au moins {inputs} entrées / {outputs} sorties (d'après les listes)",
    "mixing.dialog.close": "Fermer",
    "mixing.dialog.remove": "Retirer {name}",
    "mixing.dialog.done": "Terminé",
    "mixing.dialog.digital": "Numériques",
    "mixing.dialog.analogue": "Analogiques",
    "mixing.dialog.brandsHint": "Choisissez une marque. N'importe laquelle de ses consoles qui tient le show convient, ou ouvrez-la pour choisir des consoles précises.",
    "mixing.dialog.back": "‹ Marques",
    "mixing.dialog.anyBrand": "N'importe quelle console {brand}",
    "mixing.dialog.anyBrandMeta": "toutes celles qui tiennent le show : {fit} sur {total}",
    "mixing.tier.senior": "Haut de gamme",
    "mixing.tier.mid": "Milieu de gamme",
    "mixing.tier.entry": "Entrée de gamme",
    "mixing.desk.figures": "{inputs} ent / {outputs} sor",
    "mixing.desk.unknown": "?",
    "mixing.desk.notStated": "capacité non indiquée",
    "mixing.desk.tooSmall": "trop petite pour ce rider",
    "mixing.desk.discontinued": "n'est plus fabriquée",
    "mixing.pick.brand": "N'importe quelle {brand}",
    "gearList.bySection": "Par section",
    "gearList.byKit": "Par kit",
    "gearList.groupingLabel": "Regrouper",
    "gearList.collapseAll": "Tout replier",
    "gearList.expandAll": "Tout déplier",
    "printRider.gear.orderSummary": "À fournir : {lines} lignes, {items} pcs en {sections} sections.",
    "printRider.gear.orderSummaryBand": "Le groupe apporte {lines} autres lignes : elles figurent en fin de liste et ne sont pas à fournir.",
    "printRider.gear.youOffer": "Vous proposez :",
    "printRider.headers.offerHint": "✓ comme demandé, ou équivalent",
    "printRider.offer.date": "Date",
    "printRider.offer.city": "Ville",
    "printRider.offer.venue": "Salle",
    "printRider.offer.company": "Société de location",
    "printRider.offer.answeredBy": "Réponse de",
    "printRider.offer.weAsk": "Nous demandons",
    "printRider.offer.youOffer": "Vous proposez",
    "printRider.gear.derivedFrom": "de la liste d'entrées, voie {channels}",
    "printRider.gear.summaryLabel": "Récapitulatif",
    "printRider.gear.summaryOr": "ou :",
    "printRider.gear.inDetail": "En détail",
    "printRider.gear.sum.wedgeMixes": "Mixes de retours au sol : {count}",
    "printRider.gear.sum.iemMixes": "Mixes in-ear : {count}",
    "printRider.gear.sum.otherOutputs": "Autres sorties : {count}",
    "printRider.gear.sum.microphones": "Micros : {count}",
    "printRider.gear.sum.microphonesWireless": "Micros : {count}, dont sans fil : {wireless}",
    "printRider.gear.sum.diBoxes": "Boîtiers DI : {count}",
    "printRider.gear.sum.drumKit": "Batterie, éléments : {pieces}",
    "printRider.gear.sum.more": "et {count} de plus",
    "printRider.offer.heading": "On répond à ce rider dans le document même",
    "printRider.offer.howTitle": "Comment répondre.",
    "printRider.offer.howTick": "coché — exactement comme demandé",
    "printRider.offer.howEq": "la ligne — l'équivalent que vous proposez",
    "printRider.offer.howSummary": "Chaque section du matériel commence par un récapitulatif : cochez-le si toute la section est conforme, et ne répondez ligne par ligne que là où votre offre diffère.",
    "printRider.offer.howNo": "Ce que vous ne pouvez pas fournir : écrivez « non » et pourquoi. Une raison est une réponse ; le silence n'en est pas une.",
    "printRider.offer.stageAsk": "Scène {width} × {length} m.",
    "printRider.offer.signatures": "Signatures",
    "printRider.offer.preparedBy": "Offre préparée par (prestataire / salle)",
    "printRider.offer.acceptedBy": "Accepté pour le groupe",
    "printRider.offer.signDate": "Date",
    "printRider.offer.answerOk": "OK",
    "printRider.offer.answerSub": "Substitut : {model}",
    "printRider.offer.answerBandCarried": "Apporté par le groupe",
    "printRider.offer.answerNa": "Indisponible : {reason}",
    "printRider.offer.answerVenue": "Fourni par la salle",
    "printRider.offer.answerVenueNote": "Fourni par la salle : {note}",
    "printRider.offer.answeredHeading": "Offre de {supplier} · {status} · réponses v{round}",
    "printRider.footer.offerFor": "Offre pour : ______________________",
    "printRider.footer.page": "Page {page} sur {pages}",
    "printRider.publicLinkMissing":
        'Lien public : pas encore publiée — utilisez le bouton "Publier la fiche" (barre supérieure) pour obtenir un lien avec QR code.',
    "printRider.publicLinkLabel": "Lien public (scannez ou visitez) — lecture seule, sans connexion — v{version}",
    "printRider.qrUnavailable": "Code QR indisponible (URL trop longue) — utilisez le lien ci-dessus.",
    "printRider.qrAriaLabel": "Code QR menant vers la page publique de cette fiche technique",
    "printRider.untitledRider": "Fiche sans titre",
    "printRider.stagePlotUnavailable": "Plan de scène indisponible.",
    "printRider.stagePlotPending": "Capture de la scène…",
    "printRider.stagePlanPending": "Construction du plan…",
    "printRider.stagePlotAlt": "Plan de scène",
    "printRider.sections.stagePlan": "Plan de scène — vue de dessus",
    "printRider.stagePlanAlt": "Plan de scène, vue de dessus",
    "printRider.stagePlanUnavailable": "Plan de scène indisponible.",
    "printRider.contactLabel": "Contact : {name}",
    "printRider.telLabel": "Tél : {phone}",
    "printRider.emailLabel": "E-mail : {email}",
    "printRider.bandLogoAlt": "Logo du groupe",
    "printRider.footerPage": "Page {page} sur {pages}",
    "printRider.docTitle": "{heading} — fiche technique",
    "printRider.metaBrand": "Fiche technique SoundCheckBox",
    "printRider.idLabel": "ID : {id}",
    "printRider.unsavedId": "(non enregistrée)",
    "printRider.stageSize": "Scène : {width} × {length} m",
    "printRider.sections.stagePlot": "Plan de scène (Stage plot)",
    "printRider.sections.riderNotes": "Notes sur la fiche",
    "printRider.sections.inputList": "Liste d'entrées (Input list)",
    "printRider.sections.outputList": "Liste de sorties (Output list)",
    "printRider.sections.gearList": "Liste de matériel (Gear list)",
    // --- print settings panel (components/print-options-dialog.ts, SCRUM-96) ---
    "printVariant.heading": "Que voulez-vous imprimer ?",
  "printVariant.simpleName": "Simple View",
  "printVariant.simpleNote": "Une page paysage : le plan de scène avec les listes d’entrées et de sorties dessous.",
  "printVariant.techName": "Tech stage plot",
  "printVariant.techNote": "Une page, plan en noir et blanc — pour l'équipe technique",
  "printVariant.bothName": "Les deux feuilles",
  "printVariant.bothNote": "Deux pages : noir et blanc pour l'équipe, couleur pour l'artiste",
  "printVariant.fullName": "Tout imprimer",
  "printVariant.fullNote": "Le rider complet, chaque section sur sa propre feuille.",
  "print.fileNameSimple": "{title} — Simple View",
  "print.fileNameTech": "{title} — Tech Plot",
  "print.fileNameBoth": "{title} — Tech Plot + Simple View",
  "print.fileNameFull": "{title} — Full Details",
  "simpleView.captionSuffix": "Voir le plan de scène interactif et faire une offre pour cette fiche technique sur soundcheckbox.com",
  "simpleView.labelTech": "Tech stage plot",
  "simpleView.labelColour": "Simple view",
  "simpleView.captionUnpublished": "Fiche non publiée — {site}",
  "editor.documentTitle": "{title} — SoundCheckBox",
  "editor.documentTitleUntitled": "Fiche sans titre",
  "simpleView.plotUnavailable": "Plan de scène indisponible",
  "simpleView.overflow": "Certaines lignes n’ont pas tenu sur cette feuille.",
  "simpleView.textClipped": "La suite du texte du rider figure dans le rider imprimé complet et sur sa page publique.",
  "printOptions.heading": "Paramètres d'impression",
    "printOptions.openTitle": "Paramètres d'impression…",
    "printOptions.hint": "Choisissez ce qui figure sur la page. Laissez un en-tête de colonne vide pour garder le nom standard.",
    "printOptions.presetFull": "Rider complet",
    "printOptions.presetOnePage": "Une page (paysage)",
    "printOptions.gearLegend": "Liste de matériel",
    "printOptions.gearByCategory": "Grouper par catégorie",
    "printOptions.gearByKit": "Grouper par ensemble",
    "printOptions.pageLegend": "Page",
    "printOptions.pageBreaks": "Chaque section sur sa propre page",
    "printOptions.landscapePage": "Page paysage",
    "printOptions.alwaysLandscape": "Toujours paysage, pleine page",
    "printOptions.onCover": "Sur la couverture",
    "printOptions.coverInfo": "Informations",
    "printOptions.coverEditContacts": "Modifier",
    "printOptions.coverSaveContacts": "Enregistrer",
    "printOptions.coverBandLink": "Lien du groupe",
    "printOptions.coverOff": "non imprimée",
    "printOptions.coverMissing": "Vide : {fields}",
    "printOptions.coverMissingShort": "vide : {count}",
    "printOptions.sectionsLegend": "Sections",
    "printOptions.lookLegend": "Apparence",
    "printOptions.coverLegend": "Couverture",
    "printOptions.cover": "Imprimer la couverture",
    "printOptions.coverLogoSize": "Taille du logo",
    "printOptions.coverTextSize": "Taille du texte",
    "printOptions.coverLogoFile": "Logo",
    "printOptions.coverNoLogo": "non chargé",
    "printOptions.coverLogoUpload": "Charger",
    "printOptions.coverLogoReplace": "Remplacer",
    "printOptions.coverLogoClear": "Retirer",
    "printOptions.coverLogoFromRider": "Le logo vit dans le rider — le même apparaît sur la page publique et dans le bloc contacts",
    "printOptions.coverCheckEmpty": "vide",
    "printOptions.coverCheck.logo": "Logo",
    "printOptions.coverCheck.band": "Groupe",
    "printOptions.coverCheck.rider": "Rider",
    "printOptions.coverCheck.contact": "Contacts",
    "printOptions.coverCheck.link": "Lien du groupe",
    "printOptions.coverCheck.qr": "QR du lien public",
    "printOptions.coverLogo": "Logo",
    "printOptions.coverText": "Texte",
    "printOptions.align.left": "Gauche",
    "printOptions.align.center": "Centré",
    "printOptions.align.right": "Droite",
    "printOptions.previewTitle": "Aperçu",
    "printOptions.previewBuilding": "Génération de l'aperçu…",
    "printOptions.previewFailed": "Aperçu indisponible",
    "printOptions.style.plain": "Simple",
    "printOptions.style.classical": "Classique",
    "printOptions.style.jazz": "Jazz",
    "printOptions.style.rock": "Rock",
    "printOptions.style.metal": "Metal",
    "printOptions.style.disco": "Disco",
    "printOptions.palette.mono": "Noir et blanc",
    "printOptions.palette.graphite": "Graphite",
    "printOptions.palette.blue": "Bleu",
    "printOptions.palette.red": "Rouge",
    "printOptions.palette.amber": "Ambre",
    "printOptions.palette.violet": "Violet",
    "printOptions.inputColumnsLegend": "Colonnes de la liste d'entrées",
    "printOptions.outputColumnsLegend": "Colonnes de la liste de sorties",
    "printOptions.confirm": "Imprimer",
    "printOptions.savePdf": "Enregistrer en PDF",
    "printOptions.savedPdf": "PDF enregistré.",
    "printOptions.section.contact": "Groupe et contacts",
    "printOptions.section.publicLink": "Lien public et QR",
    "printOptions.section.powerSummary": "Puissance totale",
    "printOptions.section.stage3d": "Stage 3D view (in color)",
    "printOptions.section.techTopView": "Stage plot tech top view",

    // --- wysiwyg toolbar (components/wysiwyg-editor.ts) --------------------------------------------------
    "wysiwyg.bold": "Gras",
    "wysiwyg.italic": "Italique",
    "wysiwyg.underline": "Souligné",
    "wysiwyg.bulletedList": "Liste à puces",
    "wysiwyg.numberedList": "Liste numérotée",
    "wysiwyg.clearFormatting": "Effacer la mise en forme",
    "wysiwyg.boldLabel": "G",
    "wysiwyg.italicLabel": "I",
    "wysiwyg.underlineLabel": "S",
    "wysiwyg.bulletedListLabel": "• Liste",
    "wysiwyg.numberedListLabel": "1. Liste",
    "wysiwyg.clearFormattingLabel": "⌫ Effacer",

    // --- main.ts fatal startup overlay --------------------------------------------------------------------
    "main.fatal.heading": "Échec du démarrage de SoundCheckBox",
    "main.fatal.hint": "Envoyez ce texte au support — il permet d'identifier précisément le problème.",
    "main.fatal.userAgentLabel": "Agent utilisateur : {ua}",

    // --- editor tour & cheatsheet (components/editor-tour.ts) ---------------------------------------------
    "tour.buttonTitle": "Visite interactive et aide (?)",
    "tour.buttonLabel": "Aide",
    "tour.stepBadge": "Étape {current} sur {total}",
    "tour.closeAriaLabel": "Fermer",
    "tour.prev": "← Précédent",
    "tour.next": "Suivant →",
    "tour.finish": "Terminer",
    "tour.cheatSheetButton": "Aide-mémoire",
    "tour.cheatSheetButtonTitle": "Voir le guide complet des raccourcis et modificateurs",
    "tour.cheatSheetTitle": "Raccourcis et modificateurs de l'éditeur",
    "tour.startTourButton": "Démarrer la visite",
    "tour.closeSheetButton": "Fermer",

    "tour.steps.stageSize.title": "1. Changez la taille de la scène",
    "tour.steps.stageSize.desc": "Double-cliquez sur le sol de la scène (sur écran tactile, touchez deux fois). Tirez les poignées des bords et des coins pour régler la largeur et la profondeur de la scène ; la taille en mètres s'affiche en haut à gauche. Cliquez sur <b>Terminer</b> ou appuyez sur <b>Entrée</b>.",
    "tour.steps.addObject.title": "2. Ajoutez les musiciens et le matériel",
    "tour.steps.addObject.desc": "Choisissez un onglet en bas (<b>members</b> pour les musiciens, <b>Drums &amp; Perc</b>, <b>mic &amp; DI</b>, <b>monitors</b>, <b>backline</b>…). Cliquez sur un préréglage ou glissez-le sur la scène, puis glissez-le à sa place.",
    "tour.steps.addObject.iem": "Certains musiciens préfèrent les in-ears.",
    "tour.steps.addObject.outputs": "L'<b>Output List</b> se recalcule automatiquement.",
    "tour.steps.members.title": "3. Les musiciens et leur matériel",
    "tour.steps.members.desc": "Un musicien (<b>members</b>) est la base d'un préréglage : ses instruments, micros et DI arrivent avec lui. Parfois il est plus pratique de ne voir que le matériel sur scène : maintenez <b>Alt</b>, cliquez sur le musicien et appuyez sur <b>Delete</b> — le matériel reste.",
    "tour.steps.arrange.title": "4. Disposez la scène",
    "tour.steps.arrange.desc": "Glissez les objets à leur place. <b>Pivoter</b> affiche un anneau : tirez-le pour tourner la sélection. <b>Attacher l'objet</b>, puis un clic sur un praticable ou un pied, fixe l'objet dessus : ils bougent ensemble. <b>Toute la pile</b> / <b>Un seul objet</b> règle ce que prend un clic. Gestes : <b>Aide-mémoire</b>. La position relative du matériel et des structures de scène est parfois plus facile à ajuster en <b>Vue de dessus</b>.",
    "tour.steps.channels.title": "5. Input List et Output List",
    "tour.steps.channels.desc": "L'<b>Input List</b> et l'<b>Output List</b> se construisent automatiquement à partir de ce qui est sur scène ; saisissez simplement les noms des musiciens dans la colonne <b>Source</b>. Glissez une ligne pour changer son <b>Ch</b>. Donnez une adresse à chaque mix : saisissez dans <b>Source</b> pour qui chaque retour est placé.",
    "tour.steps.micDi.title": "6. Micros et DI",
    "tour.steps.micDi.desc": "Les micros et les DI arrivent avec les préréglages. Cliquez sur la cellule <b>Micro / DI</b> d'une ligne et choisissez ceux que vous préférez dans la liste déroulante.",
    "tour.steps.planSheet.title": "7. Vérifiez le Plan",
    "tour.steps.planSheet.desc": "Passez en <b>Plan</b> et vérifiez l'allure de votre fiche en dessin : c'est ainsi que l'équipe technique la verra sur scène. Les objets peuvent aussi être déplacés ici.",
    "tour.steps.riderFill.title": "8. Remplissez la fiche et enregistrez-la",
    "tour.steps.riderFill.desc": "Dans l'onglet <b>Fiche</b> à droite : rédigez le <b>Texte de la fiche / Notes</b>, saisissez le <b>Titre</b> de la fiche et le <b>Nom du groupe / artiste</b>, ajoutez le logo via <b>Fichier du logo</b>. Puis cliquez en haut sur <b>Enregistrer la fiche technique dans la base de données</b>.",
    "tour.steps.publishPrint.title": "9. Publier et imprimer",
    "tour.steps.publishPrint.desc": "Une fois la fiche enregistrée, <b>Publier la fiche</b> crée un lien en lecture seule pour la salle et le copie. <b>PDF</b> demande <b>Que voulez-vous imprimer ?</b> : <b>Simple View</b>, <b>Tech stage plot</b> ou <b>Les deux feuilles</b> ; <b>Tout imprimer</b> ouvre les <b>Paramètres d'impression</b> avec un aperçu.",
    "tour.end.title": "Vider la scène ou continuer ?",
    "tour.end.desc": "La visite a placé du matériel sur la scène. La vider, ou continuer avec cette scène ?",
    "tour.end.clean": "Vider la scène",
    "tour.end.keep": "Continuer avec cette scène",
    "tour.banner.text": "Première visite ? Faites un tour rapide : le programme montre chaque étape sur la scène.",
    "tour.banner.start": "Lancer la visite",
    "tour.banner.close": "Fermer",

    "tour.cs.sectionNav": "Navigation 3D",
    "tour.cs.dragOrbit": "Tourner l'orbite de caméra",
    "tour.cs.dragPan": "Déplacement dans le plan",
    "tour.cs.zoom": "Zoom avant / arrière",
    "tour.cs.topView": "Basculer Vue du dessus / 3D",

    "tour.cs.sectionModifiers": "Gestes et modificateurs",
    "tour.cs.clickDefault": "Sélectionner et déplacer le groupe ou praticable",
    "tour.cs.altClick": "Sélectionner un objet individuel dans le groupe",
    "tour.cs.altDrag": "Déplacer un objet sans bouger le groupe",
    "tour.cs.shiftGlue": "Attacher l'objet glissé à la cible",
    "tour.cs.altShiftDetach": "Détacher l'objet du groupe sur place",
    "tour.cs.rotateGizmo": "Tourner le groupe (Alt pour tourner l'objet individuel)",

    "tour.cs.sectionObjectOps": "Opérations d'objet",
    "tour.cs.opProperties": "Ouvrir les paramètres et canaux audio",
    "tour.cs.opRotate": "Activer l'anneau de rotation interactif",
    "tour.cs.opAttach": "Lier l'objet sélectionné à un autre",
    "tour.cs.opSavePreset": "Enregistrer le groupe en preset utilisateur",
    "tour.cs.opDelete": "Supprimer cet objet (les enfants sont conservés)",
    "tour.cs.opDeleteTree": "Supprimer l'objet et tous ses enfants",

    "tour.cs.sectionHotkeys": "Raccourcis clavier",
    "tour.cs.keyProperties": "Afficher / masquer les propriétés de l'objet",
    "tour.cs.keyRotate": "Afficher / masquer l'anneau de rotation",
    "tour.cs.keyInputList": "Afficher / masquer Input List",
    "tour.cs.keyOutputList": "Afficher / masquer Output List",
    "tour.cs.keyUndo": "Annuler l'action",
    "tour.cs.keyRedo": "Rétablir l'action",
    "tour.cs.keySave": "Enregistrer le rider",
    "tour.cs.keyEscape": "Désélectionner / Fermer la boîte de dialogue",
    "sideNav.shows": "Spectacles",
    "shows.pageTitle": "Spectacles",
    "shows.tab.inbox": "Boîte de réception",
    "shows.tab.calendar": "Calendrier",
    "shows.tabsLabel": "Rubriques des spectacles",
    "shows.tab.list": "Spectacles",
    "shows.list.label": "Tous les spectacles",
    "shows.list.col.date": "Date",
    "shows.list.col.band": "Groupe",
    "shows.list.col.venue": "Salle",
    "shows.list.col.city": "Ville",
    "shows.list.col.rental": "Location",
    "shows.list.col.status": "Statut",
    "shows.list.empty": "Aucun spectacle pour l'instant.",
    "shows.list.emptyFiltered": "Aucun spectacle pour cette sélection.",
    "shows.list.rentalOne": "1 offre",
    "shows.list.rentalMany": "{n} offres",
    "shows.list.rentalNone": "Pas encore d'offre",
    "shows.list.rentalYou": "Vous",
    "shows.list.dateOpen": "Date pas encore fixée",
    "shows.list.state.sketch": "Ébauche",
    "shows.mcal.label": "Calendrier des spectacles",
    "shows.mcal.prev": "Mois précédent",
    "shows.mcal.next": "Mois suivant",
    "shows.mcal.showAll": "Tout afficher",
    "shows.mcal.filterDay": "Spectacles du {day}",
    "shows.mcal.filterMonth": "Spectacles de {month}",
    "shows.mcal.today": "aujourd'hui",
    "shows.loadFailed": "Impossible de charger vos spectacles : {error}",
    "shows.inbox.empty": "Rien n'attend votre réponse pour le moment.",
    "shows.inbox.listLabel": "Ce qui attend votre réponse",
    "shows.inbox.unknownWho": "Un prestataire",
    "shows.inbox.approvalNew": "{who} a envoyé une offre pour {subject} ({when}). Elle attend votre approbation.",
    "shows.inbox.approvalAgain": "{who} a renvoyé l'offre pour {subject} ({when}). Elle attend votre approbation.",
    "shows.inbox.newOffer": "{who} a envoyé une offre pour {subject} ({when}).",
    "shows.inbox.reconfirm": "Le rider a changé (v{from} à v{to}). Reconfirmez votre offre pour {subject} ({when}).",
    "shows.inbox.declined": "Votre offre pour {subject} ({when}) a été refusée.",
    "shows.inbox.declinedReason": "Motif : {reason}",
    "shows.inbox.riderRequest": "Un rider a été demandé pour {subject} ({when}).",
    "shows.inbox.open": "Ouvrir",
    "shows.inbox.dateOpen": "date pas encore fixée",
    "shows.inbox.received": "Reçu {at}",
    "shows.inbox.new": "Nouveau",
    "sideNav.unreadCount": "{n} non lus",
    "shows.cal.month": "Mois",
    "shows.cal.week": "Semaine",
    "shows.cal.list": "Liste",
    "shows.cal.today": "Aujourd'hui",
    "shows.cal.prev": "Précédent",
    "shows.cal.next": "Suivant",
    "shows.cal.noEvents": "Aucun spectacle sur cette période",
    "shows.cal.waiting": "En attente d'une date",
    "shows.cal.waitingHint": "Les spectacles sans date ne figurent pas dans la grille.",
    "shows.cal.waitingEmpty": "Rien n'attend de date.",
    "shows.cal.sketch": "Ébauche",
    "shows.cal.untitled": "Spectacle sans titre",
    "shows.feed.title": "S'abonner dans votre agenda",
    "shows.feed.hint": "Google, Apple et Outlook acceptent cette adresse via « s'abonner par URL ».",
    "shows.feed.hidden": "Votre lien est actif. Par sécurité, il n'est affiché qu'à sa création — renouvelez-le pour en obtenir un nouveau.",
    "shows.feed.addressLabel": "Votre adresse d'agenda privée",
    "shows.feed.create": "Créer le lien",
    "shows.feed.rotate": "Renouveler",
    "shows.feed.rotateConfirm": "Cliquez à nouveau : l'ancien lien cessera de fonctionner",
    "shows.feed.turnOff": "Désactiver",
    "shows.feed.turnOffConfirm": "Cliquez à nouveau pour désactiver",
    "shows.feed.failed": "Impossible de mettre à jour le lien de l'agenda : {error}",
    "shows.detail.back": "Spectacles",
    "shows.detail.loadFailed": "Impossible de lire ce spectacle : {error}",
    "shows.detail.state.open": "Ouvert",
    "shows.detail.state.waiting": "En attente d'offres",
    "shows.detail.state.offers_in": "Offres reçues",
    "shows.detail.state.agreed": "Convenu",
    "shows.detail.state.past": "Passé",
    "shows.detail.acts": "Prestations",
    "shows.detail.noActs": "Aucun artiste sur ce spectacle pour l'instant.",
    "shows.detail.riderVersion": "Rider v{version}",
    "shows.detail.noRider": "Pas encore de rider",
    "shows.detail.newerVersion": "Une version plus récente existe : v{version}",
    "shows.detail.getLink": "Afficher le lien du spectacle",
    "shows.detail.linkFailed": "Impossible d'obtenir le lien : {error}",
    "shows.detail.sketchTitle": "Ébauche — pas encore de date ni de salle",
    "shows.detail.sketchHint": "Indiquez une date ou une salle et il devient un spectacle ordinaire.",
    "shows.detail.sketchWhere": "Date et salle",
    "shows.detail.change": "Modifier la date et le lieu",
    "shows.detail.changeNeedsDate": "Choisissez une date : le show en a déjà une.",
    "shows.detail.sketchSave": "Enregistrer la date et la salle",
    "shows.detail.sketchNeeds": "Choisissez d'abord une date ou une salle.",
    "shows.detail.saveFailed": "Impossible d'enregistrer : {error}",
    "shows.offers.heading": "Offres",
    "shows.offers.empty": "Pas encore d'offres.",
    "shows.offers.sortLabel": "Trier par",
    "shows.offers.sort.time": "Heure (envoyée)",
    "shows.offers.sort.match": "Correspondance",
    "shows.offers.sort.fewestMissing": "Moins de manquants",
    "shows.offers.sort.price": "Prix",
    "shows.offers.sort.priceNotYet": "pas encore",
    "shows.offers.match": "{n} correspondent",
    "shows.offers.missing": "{n} manquent",
    "shows.offers.riderChanged": "Rider modifié v{from} → v{to}",
    "shows.offers.open": "Ouvrir l'offre",
    "shows.offers.organiserApprove": "Approuver comme organisateur",
    "shows.offers.organiserRequestChanges": "Demander des modifications comme organisateur",
    "shows.offers.actionFailed": "Impossible à faire : {error}",
    // --- crew: the hired worker's screen and the admin's test link (SCRUM-762) ---
    "crew.untitledShow": "Spectacle",
    "crew.dateOpen": "Date non définie",
    "crew.venueNone": "Lieu non défini",
    "crew.signInWhy": "C'est une mission sur un spectacle. Connectez-vous ou créez un compte pour la prendre : cela prend une minute.",
    "crew.signInToTake": "Connectez-vous pour prendre cette mission",
    "crew.take": "Prendre cette mission",
    "crew.takenBySomeone": "Quelqu'un d'autre a déjà pris cette mission. Demandez la vôtre à la personne qui vous a envoyé le lien.",
    "crew.gone": "Ce lien de mission n'existe pas ou a été retiré.",
    "crew.failed": "Une erreur s'est produite : {error}",
    "crew.saveFailed": "La coche n'a pas été enregistrée : {error}",
    "crew.noRider": "Ce spectacle n'a pas encore de rider publié, il n'y a donc rien à cocher.",
    "crew.riderGone": "Le rider de ce spectacle n'est plus publié, les listes sont donc vides. Les étapes restent.",
    "crew.riderMoved": "Le rider a été mis à jour après l'accord sur ce spectacle — ces listes sont les nouvelles.",
    "crew.riderMovedOn": "Rider mis à jour le {date} — plus récent que ce qui a été convenu pour ce spectacle. Ces listes sont les nouvelles.",
    "crew.tab.out": "Prépa",
    "crew.tab.venue": "Lieu",
    "crew.tab.return": "Retour",
    "crew.tab.plan": "Plan",
    "crew.time.warehouse": "Dépôt",
    "crew.time.truck": "Camion",
    "crew.time.venue": "Lieu",
    "crew.time.showRun": "Spectacle",
    "crew.time.strike": "Démontage",
    "crew.thisPart": "Cette partie",
    "crew.doneOf": "{done} sur {total}",
    "crew.done": "Fait",
    "crew.twoPeople": "Porter à deux",
    "crew.showRunTwo": "2 personnes",
    "crew.kit.inside": "Contenu",
    "crew.kit.cablesApart": "Les câbles sont comptés dans le bloc Câbles",
    "crew.kit.included": "dans la boîte",
    "crew.kit.consumablesApart": "Les piles et consommables sont comptés sur leur propre ligne",
    "crew.truckFront": "AVANT DU CAMION",
    "crew.missing": "Manquant",
    "crew.broken": "Cassé",
    "crew.missingOpen": "MANQUANT — reste ouvert",
    "crew.brokenOpen": "CASSÉ — reste ouvert",
    "crew.less": "Un de moins",
    "crew.more": "Un de plus",
    "crew.tickAria": "{name} : {done} sur {total}",
    "crew.hint.fromOffer": "Absent de ce rider — vient de l'offre du loueur",
    "crew.hint.nothing": "Rien ici",
    "crew.returnOpen": "{n} lignes encore ouvertes (non rentrées, manquantes ou cassées). Elles restent sur la liste du loueur — rien ne disparaît.",
    "crew.returnAllBack": "Tout est rentré.",
    // SCRUM-760: «Loading done — depart» gate. Translated like the neighbouring crew.* keys.
    "crew.gate.depart": "Chargement terminé — partir",
    "crew.gate.departVenue": "Chargé — quitter le lieu",
    "crew.gate.confirm": "Le camion part maintenant ? Appuyez encore",
    "crew.gate.waiting": "On attend que le chauffeur donne le départ",
    "crew.gate.departed": "Parti à {time} · {name}",
    "crew.gate.departedNoName": "Parti à {time}",
    "crew.gate.by.driver": "Envoyé par le chauffeur",
    "crew.gate.by.supplier": "Envoyé par la société de location",
    "crew.gate.locked": "Le camion est parti : le chargement est clos. Demandez au chauffeur de le rouvrir s'il manque quelque chose.",
    "crew.gate.next": "Continuer — {tab}",
    "crew.gate.reopen": "Rouvrir le chargement",
    "crew.gate.failed": "Impossible de mettre à jour le départ : {error}",
    "crew.zoom": "Zoom",
    "crew.fit": "Ajuster",
    "crew.legendDone": "fait {n}",
    "crew.legendPart": "partiel {n}",
    "crew.legendTodo": "à faire {n}",
    "crew.legendMiss": "manquant / cassé {n}",
    "crew.planHint": "Les coches se posent par blocs ; le plan les montre par couche, dans l'ordre de la journée.",
    "crew.changeover.title": "Changements de plateau entre les passages",
    "crew.changeover.heading": "Changement de plateau {from} → {to}",
    "crew.changeover.headingMin": "Changement de plateau {from} → {to} ({n} min)",
    "crew.changeover.none": "Rien à déplacer : ce dont le passage suivant a besoin est déjà en place.",
    "crew.changeover.recheck": "Puis line check, canaux : {channels}",
    "crew.zone.USR": "Fond droite",
    "crew.zone.USC": "Fond centre",
    "crew.zone.USL": "Fond gauche",
    "crew.zone.SR": "Droite",
    "crew.zone.CS": "Centre de la scène",
    "crew.zone.SL": "Gauche",
    "crew.zone.DSR": "Devant droite",
    "crew.zone.DSC": "Devant centre",
    "crew.zone.DSL": "Devant gauche",
    "crew.step.toWingsStand": "Emporter {item} sur son pied en coulisses, câble branché",
    "crew.step.rollOut": "Sortir {item} en coulisses",
    "crew.step.back": "Ramener {item} des coulisses",
    "crew.step.swapStand": "Retirer {from}, mettre {to} sur le même pied",
    "crew.step.replace": "Remplacer {from} par {to}",
    "crew.step.add": "Installer {item}",
    "crew.step.bandOut": "Retirer de la scène le matériel de {act} : {items}",
    "crew.step.bandIn": "Installer le matériel de {act} : {items}",
    "crew.layer.picked": "Préparé",
    "crew.layer.loaded": "Chargé",
    "crew.layer.placed": "Installé",
    "crew.layer.patched": "Patché",
    "crew.layer.checked": "Vérifié",
    "crew.layer.unpatched": "Dépatché",
    "crew.layer.struck": "Démonté",
    "crew.layer.loadedback": "Rechargé",
    "crew.layer.returned": "Rentré",
    "crew.phase.pick": "Préparation au dépôt",
    "crew.phase.pickSub": "Ordre de déchargement — le camion se charge à l'envers",
    "crew.phase.load": "Charger le camion",
    "crew.phase.loadSub": "Ordre inverse · le lourd à l'avant du camion",
    "crew.phase.setup": "Décharger et installer",
    "crew.phase.setupSub": "Sur le lieu",
    "crew.phase.showRun": "Spectacle",
    "crew.phase.showRunSub": "L'équipe reste de service — tâches de scène plus tard",
    "crew.phase.strike": "Démontage et chargement",
    "crew.phase.strikeSub": "Après le spectacle",
    "crew.phase.back": "Retour au dépôt",
    "crew.phase.backSub": "Décharger et pointer — ce qui est rentré, manquant, cassé",
    "crew.block.carts": "Chariots et rampe",
    "crew.block.racks": "Racks et alimentation",
    "crew.block.pa": "P.A.",
    "crew.block.mon": "Retours",
    "crew.block.foh": "FOH",
    "crew.block.mics": "Micros",
    "crew.block.dis": "DI",
    "crew.block.stands": "Pieds",
    "crew.block.backline": "Backline",
    "crew.block.risers": "Praticables et mobilier",
    "crew.block.cables": "Câbles",
    "crew.block.other": "Autre",
    "crew.block.loadOut": "Chargement · {block}",
    "crew.block.unloadToPositions": "Décharger aux positions",
    "crew.block.unloadToPositionsSub": "Le lourd directement en place, le FOH à sa position",
    "crew.block.paMonitorsInPlace": "P.A. et retours en place",
    "crew.block.powerPa": "Alimentation P.A.",
    "crew.block.powerStage": "Alimentation scène",
    "crew.block.powerFoh": "Alimentation FOH",
    "crew.block.paCheck": "Test P.A. et front fills",
    "crew.block.stageSet": "Scène selon le rider",
    "crew.block.stageSetSub": "En parallèle du test P.A.",
    "crew.block.patch": "Patch",
    "crew.block.patchSub": "Chaque entrée de la liste d'entrées",
    "crew.block.lineCheck": "Line check",
    "crew.block.lineCheckSub": "L'ingé son du groupe arrive et lance les balances",
    "crew.block.showRun": "Spectacle",
    "crew.block.showRunSub": "Sur scène pendant le spectacle",
    "crew.block.showRunN": "Spectacle {n}",
    "crew.banner.powerOn": "Allumage : d'abord les sources (console, stage rack, HF), puis les amplis.",
    "crew.banner.powerOff": "Extinction : d'abord les amplis, puis les sources (console, stage rack, HF).",
    "crew.block.unpatch": "Dépatch",
    "crew.block.clearSmall": "Vider la scène · petit, léger et câbles",
    "crew.block.clearSmallSub": "D'abord micros, DI, pieds, câbles",
    "crew.block.clearLarge": "Vider la scène · gros",
    "crew.block.clearLargeSub": "Amplis, praticables, piano, retours",
    "crew.block.fohPaLast": "FOH et P.A.",
    "crew.block.fohPaLastSub": "Dernier à partir — premier à l'installation",
    "admin.crew.title": "Lien équipe pour vous (test)",
    "admin.crew.hint": "Vous place comme équipier sur l'un de vos spectacles, pour essayer l'écran de l'équipier depuis votre téléphone. Ouvrez le lien sur le téléphone et prenez la mission.",
    "admin.crew.show": "Spectacle",
    "admin.crew.make": "Créer un lien équipe",
    "admin.crew.made": "Lien créé. Ouvrez-le sur votre téléphone.",
    "admin.crew.link": "Lien équipe",
    "admin.crew.open": "Ouvrir",
    "admin.crew.noShows": "Vous n'avez pas encore de spectacle — créez-en un d'abord dans Spectacles",
    "admin.crew.dateOpen": "sans date",
    "admin.crew.failed": "Impossible à faire : {error}",
    "admin.perf.title": "Ajouter un passage à un show",
    "admin.perf.hint": "En fait un show à plusieurs passages : l'onglet « Salle » du lien équipe montre alors les changements de plateau entre les passages. Utilise le show choisi plus haut.",
    "admin.perf.name": "Nom du passage",
    "admin.perf.rider": "Rider (publié)",
    "admin.perf.noRider": "Pas encore de rider",
    "admin.perf.add": "Ajouter le passage",
    "admin.perf.added": "Ajouté. Le show compte maintenant {n} passages.",
    "admin.perf.failed": "Impossible de l'ajouter : {error}",
    "admin.perf.needName": "Donnez un nom au passage",

    // --- rental desk (rental/desk-answer-panel.ts, rental/desk-shows-panel.ts, pages/warehouse-tabs.ts, SCRUM-647) ---
    "rental.tabsLabel": "Rubriques du matériel",
    "rental.loadFailed": "Chargement impossible : {error}",
    "rental.toAnswer.heading": "À répondre",
    "rental.toAnswer.empty": "Rien ne vous attend : toutes les offres ont une réponse.",
    "rental.cover.positions": "{n} postes",
    "rental.cover.positionsOne": "{n} poste",
    "rental.cover.ok": "{n} dans votre matériel",
    "rental.cover.short": "{n} en nombre insuffisant",
    "rental.cover.missing": "{n} manquants",
    "rental.cover.answered": "répondus {n}",
    "rental.cover.emptyWarehouse": "Votre matériel est vide : ajoutez-en pour voir la couverture",
    "rental.reason.draft": "Pas encore envoyée",
    "rental.reason.changes": "Modifications demandées",
    "rental.reason.reconfirm": "Le rider a changé — reconfirmez",
    "rental.shows.heading": "Mes shows",
    "rental.shows.empty": "Aucun show pour l’instant. Un show apparaît ici dès que vous envoyez une offre pour une date.",
    "rental.shows.dateOpen": "Date non fixée",
    "rental.shows.venueNone": "Lieu non fixé",
    "rental.shows.myOffer": "Mon offre : {status}",
    "rental.crew.make": "Lien pour l’équipe",
    "rental.crew.making": "Création…",
    "rental.crew.newLink": "Nouveau lien pour l’équipe — envoyez-le à un technicien",
    "rental.crew.linksHeading": "Liens pour l’équipe",
    "rental.crew.free": "Pas encore pris",
    "rental.crew.takenBy": "Pris par {name}",
    "rental.crew.taken": "Pris",
    "rental.crew.failed": "Impossible : {error}",
    "rental.crew.withdraw": "Retirer",
    "rental.crew.withdrawSure": "Retirer ? Appuyez encore",
    "rental.crew.withdrawFailed": "Impossible de retirer le lien : {error}",
    // SCRUM-760: driver link, role on a link row, the departure line. Translated like the neighbouring rental.crew.* keys.
    "rental.crew.driver": "Lien pour le chauffeur",
    "rental.crew.role.crew": "Équipe",
    "rental.crew.role.driver": "Chauffeur",
    "rental.crew.takeMyself": "Le prendre moi-même",
    "rental.crew.takenByYou": "Pris par vous",
    "rental.crew.openMine": "Ouvrir",
    "rental.depart.out": "Parti du dépôt",
    "rental.depart.venue": "Parti du lieu",
    "rental.depart.reopen": "Rouvrir le chargement",
    "rental.depart.failed": "Impossible de mettre à jour le départ : {error}",
    "rental.progress.heading": "Avancement",
    "rental.tasks.heading": "Ce que fait l'équipe",
    "rental.tasks.blocks": "{n} blocs",
    "rental.tasks.template": "Notre ordre de travail",
    "rental.tasks.templateNote": "Les mêmes étapes à chaque concert. Un concert donné y ajoute les quantités : ce qu'il faut sortir, patcher et vérifier d'après le rider.",
    "rental.progress.doneOf": "{done} sur {total}",
    "rental.progress.noLinks": "Pas encore de lien pour l’équipe — l’avancement apparaît dès qu’un technicien coche.",
    "rental.progress.noRider": "Pas de rider publié sur ce show — seules les étapes comptent.",
    "rental.progress.failed": "L’avancement n’a pas pu se charger : {error}",
};